import { supabase } from './supabase'
import { reportApiFailure } from './observability'
import type { BookingStatus, ResponsePart } from './render'
import { envValue, requiredEnvValue } from './env'
import type { FundProviderNumber } from './practitioner'

const supabaseUrl = requiredEnvValue(process.env.EXPO_PUBLIC_SUPABASE_URL)

// ─── Tenant context ────────────────────────────────────────────────────────────
// Set by the [tenant] layout or useRole when the tenant is resolved.
// slug takes precedence over schema for routing.

export interface TenantContext { slug?: string; schema?: string }

/**
 * Which tenant a SINGLE-TENANT deployment names, from its two build variables.
 *
 * BOTH are read, because a deployment that sets only one of them must still
 * work. Only the schema was read before, so setting only the slug sent NO
 * tenant header at all — and the slug is the name an operator reaches for,
 * since `Login.tsx` already reads `EXPO_PUBLIC_TENANT_SLUG` for branding.
 *
 * What makes that silent is who it breaks. An OPERATOR is matched against
 * `tenant_operators`/`owner_email` in v1-tenant-config BEFORE any tenant header
 * is consulted, so the whole app works for the person who would investigate. A
 * CUSTOMER is only ever found inside an already-resolved tenant, so every
 * client signing in anywhere other than a `/:slug` URL — every password reset,
 * every signup callback, every bookmark of the bare domain — gets a 401 out of
 * role detection and cannot reach the app at all. Production ran that way for
 * months with `EXPO_PUBLIC_TENANT_SCHEMA` holding the SLUG value: 2 of 4,539
 * customers had ever resolved a role, both via a slug URL.
 *
 * Pure, because `vite.config.ts` inlines these as string literals and a test
 * cannot vary them at the module scope below.
 */
export function envTenantContext(slug?: string, schema?: string): TenantContext {
  return { slug: envValue(slug), schema: envValue(schema) }
}

let _tenantContext: TenantContext = envTenantContext(
  process.env.EXPO_PUBLIC_TENANT_SLUG,
  process.env.EXPO_PUBLIC_TENANT_SCHEMA,
)

export function setTenantContext(ctx: TenantContext) {
  _tenantContext = ctx
}

function tenantHeaders(): Record<string, string> {
  if (_tenantContext.slug) return { 'x-tenant-slug': _tenantContext.slug }
  if (_tenantContext.schema) return { 'x-tenant-schema': _tenantContext.schema }
  return {}
}

// ─── Chat API ──────────────────────────────────────────────────────────────────

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

/**
 * The customer a chat turn resolved beyond doubt (one find_customer match, or a
 * read/create keyed on one person). v1-chat echoes it so the composer's customer
 * pin can follow the conversation — searching in chat and pinning in the
 * composer are two views of one decision about who this is about. Absent when
 * the turn resolved nobody, several people, or the already-pinned customer.
 */
export interface ResolvedCustomer {
  id: string
  name: string | null
  phone: string | null
}

export interface ChatApiResponse {
  parts: ResponsePart[]
  resolved_customer?: ResolvedCustomer
}

export interface AttachedFile {
  name: string
  mimeType: string
  data: string // base64
  size: number
}

export const MAX_FILE_BYTES = 5 * 1024 * 1024 // 5 MB per file
export const MAX_ATTACHMENTS = 10

// Files ride to the model as base64 inline data, which costs 4/3 of the raw
// bytes. Providers cap the request rather than the file — Gemini refuses an
// inline-data request over 20 MB, and the limit is the same order on the
// OpenRouter path — so max-size files (encoded) would be refused upstream, not
// by us.
// Capping the COMBINED raw bytes at 12 MB (≈16 MB encoded) stays inside that
// ceiling with headroom for the transcript. Ten photos fit because images are
// downscaled client-side before attaching (useFileAttachment).
export const MAX_TOTAL_FILE_BYTES = 12 * 1024 * 1024

export async function sendChatMessage(
  messages: ChatMessage[],
  files?: AttachedFile[],
  noteStyleId?: string,
  noteCustomerId?: string,
): Promise<ChatApiResponse> {
  const session = await supabase.auth.getSession()
  const jwt = session.data.session?.access_token

  if (!jwt) {
    throw new Error('Not authenticated')
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/v1-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${jwt}`,
      ...tenantHeaders(),
    },
    // `file` is the legacy single-attachment field. It is still sent — the web
    // bundle and the edge function deploy independently, so a browser running
    // the new client against a not-yet-redeployed v1-chat must not silently
    // lose the attachment. The new server prefers `files` and ignores it.
    // note_style_id selects a saved note style (operator only) — v1-chat resolves
    // it server-side and injects that style's summarisation instructions.
    // note_customer_id pins WHO the notes/attachments are about (operator only) —
    // v1-chat resolves it server-side, injects the customer into the system
    // prompt, and auto-saves media attachments to that customer's record.
    body: JSON.stringify({ messages, files, file: files?.[0], note_style_id: noteStyleId, note_customer_id: noteCustomerId }),
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const { reason, detail } = (err as { error?: { reason?: string; detail?: string } }).error ?? {}
    // Chat is the app's primary surface — a 5xx here is a user staring at a
    // failed message, which is worth an alert on its own.
    reportApiFailure({ path: 'v1-chat', method: 'POST', status: response.status })
    throw new Error(detail ?? reason ?? 'chat_error')
  }

  return response.json() as Promise<ChatApiResponse>
}

// ─── Tenant config API ─────────────────────────────────────────────────────────

export type UserRole = 'owner' | 'staff' | 'customer'

export interface TenantBranding {
  /** The business's own name. Part of branding, not incidental to it: it is what
   *  the browser tab and the installed app are called, and it supplies the
   *  initial for the fallback mark when a tenant has uploaded no logo. Keeping
   *  it here is what lets BrandingProvider brand the document by itself rather
   *  than each layout having to remember to. */
  name: string | null
  primaryColor: string
  logoUrl: string | null
  tagline: string | null
  welcomeMessage: string | null
  /** Names the tenant's own install manifest; absent leaves the manifest as it is. */
  slug?: string | null
}

export type TenantPlan = 'solo' | 'studio' | 'franchise'

/** One feature's answer. `allowed` is the only thing a screen should branch on. */
export interface FeatureEntitlement {
  allowed: boolean
  reason: 'subscription_required' | 'plan_required' | null
  required_plan: TenantPlan | null
  current_plan: TenantPlan
  subscribed: boolean
  /** Words for an owner, written server-side. Empty when allowed. */
  message: string
}

export interface TenantEntitlements {
  plan: TenantPlan
  plan_label: string
  subscribed: boolean
  features: {
    comms: FeatureEntitlement
    staff_accounts: FeatureEntitlement
    multi_site: FeatureEntitlement
  }
}

export interface TenantConfig {
  id: string
  name: string
  schema_name: string
  slug: string | null
  timezone: string
  currency: string
  setup_complete: boolean
  schema_ready: boolean
  owner_name: string | null
  owner_email: string | null
  business_type: string | null
  business_location: string | null
  cancellation_policy: string | null
  default_slot_interval_minutes: number
  /**
   * Business-wide minimum booking notice in minutes, or null for none. A FLOOR
   * under each service's own `min_notice_minutes` — the effective notice is
   * whichever is greater — and it binds customers only.
   */
  default_min_notice_minutes: number | null
  /**
   * Whether CUSTOMERS may book several treatments as one back-to-back visit.
   * ABSENT READS AS FALSE — it changes what every client sees in the booking
   * flow, so "not known" must resolve to the unchanged behaviour. Never gates an
   * operator: the business can chain treatments for somebody either way.
   */
  allow_multi_service_booking?: boolean
  user_role: UserRole
  permissions: string[]
  /**
   * What this business has paid for, DECIDED SERVER-SIDE. Never re-derive a gate
   * from `plan` and `status` in a screen: three surfaces deciding separately
   * whether comms are on is three chances to offer a button that can only fail,
   * and the sentence ships with the answer so no screen writes plan copy either.
   *
   * Absent on a customer payload and on an older server — every consumer must
   * treat "not known" as unrestricted, because the API is the real gate and a
   * screen that hides a working feature is the worse failure here.
   */
  entitlements?: TenantEntitlements
  brand_primary_color: string
  brand_logo_url: string | null
  brand_tagline: string | null
  customer_welcome_message: string | null
  customer_app_url: string | null
  // Optional: absent from responses that predate the columns.
  ga_measurement_id?: string | null
  google_ads_id?: string | null
  google_ads_conversion_label?: string | null
}

export function brandingFromConfig(config: TenantConfig): TenantBranding {
  return {
    name: config.name ?? null,
    primaryColor: config.brand_primary_color ?? '#3b82f6',
    logoUrl: config.brand_logo_url ?? null,
    tagline: config.brand_tagline ?? null,
    welcomeMessage: config.customer_welcome_message ?? null,
    slug: config.slug ?? null,
  }
}

export async function getTenantConfig(tenantSlug?: string): Promise<TenantConfig> {
  const session = await supabase.auth.getSession()
  const jwt = session.data.session?.access_token

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${jwt ?? ''}`,
  }

  if (tenantSlug) {
    headers['x-tenant-slug'] = tenantSlug
  } else {
    Object.assign(headers, tenantHeaders())
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/v1-tenant-config`, { headers })

  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: TenantConfigFailure } | null
    throw new TenantConfigError(body?.error ?? null)
  }
  return response.json() as Promise<TenantConfig>
}

/** The error envelope v1-tenant-config answers with — `customer_not_found` carries who and where. */
export interface TenantConfigFailure {
  reason?: string
  tenant_slug?: string | null
  business_name?: string | null
  email?: string | null
}

/** A failed role lookup that still says WHY, so "no client record" is never shown as a network fault. */
export class TenantConfigError extends Error {
  readonly failure: TenantConfigFailure | null
  constructor(failure: TenantConfigFailure | null) {
    super('Failed to load tenant config')
    this.name = 'TenantConfigError'
    this.failure = failure
  }
}

export async function createTenantSchema(): Promise<void> {
  const session = await supabase.auth.getSession()
  const jwt = session.data.session?.access_token
  if (!jwt) throw new Error('Not authenticated')

  const response = await fetch(`${supabaseUrl}/functions/v1/v1-tenant-config`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${jwt}`,
    },
    body: JSON.stringify({ action: 'create_schema' }),
  })

  if (!response.ok) throw new Error('Failed to create tenant schema')
}

/**
 * Everything PATCH /v1-tenant-config accepts.
 *
 * Kept in step with the endpoint's own allowlist rather than with TenantConfig:
 * several of these (the tax identity, the reminder offsets, the payment default)
 * are writable but are NOT on the read type, and the previous parameter type
 * admitted only about half of them — which is why a writable Settings screen
 * could not reach the ABN, the business address or the sender address at all.
 *
 * Deliberately absent: the worldline_* and twilio_* credentials. Those are not
 * business configuration and are not patchable from any surface.
 */
export interface TenantConfigPatch {
  name?: string
  timezone?: string
  currency?: string
  business_type?: string | null
  business_location?: string | null
  cancellation_policy?: string | null
  owner_name?: string | null
  owner_email?: string | null
  phone_format_hint?: string | null
  slug?: string | null
  setup_complete?: boolean
  default_slot_interval_minutes?: number
  /** Business-wide minimum booking notice, in minutes. null or 0 removes it. */
  default_min_notice_minutes?: number | null
  /**
   * Whether CUSTOMERS may book several treatments as one back-to-back visit.
   * Strictly boolean — the endpoint refuses anything else rather than coercing,
   * because a truthy string silently becoming a working-looking setting changes
   * what every client of the business is offered.
   */
  allow_multi_service_booking?: boolean
  /** Minutes before the appointment. `[]` switches the channel off entirely. */
  reminder_offsets_email?: number[]
  reminder_offsets_sms?: number[]
  brand_primary_color?: string | null
  brand_logo_url?: string | null
  brand_tagline?: string | null
  customer_welcome_message?: string | null
  customer_app_url?: string | null
  ga_measurement_id?: string | null
  google_ads_id?: string | null
  google_ads_conversion_label?: string | null
  email_from_address?: string | null
  email_from_name?: string | null
  /** Invoice / tax identity. The ABN is checksum-validated server-side. */
  abn?: string | null
  business_legal_name?: string | null
  business_address?: string | null
  business_phone?: string | null
  gst_registered?: boolean
  invoice_footer_note?: string | null
  default_payment_method?: 'package_credit' | 'payment_link'
  /** Replaces the whole map, so removing one shortcut means sending the rest. */
  service_aliases?: Record<string, string>
}

/**
 * Routed through `request()` like every other verb, which is the point: the
 * hand-rolled fetch this replaces threw a bare 'Failed to update tenant config',
 * so readApiError() discarded the server's real message — 'abn must be 11 digits
 * and pass the ATO checksum' became a shrug. It also skipped reportApiFailure,
 * so a 5xx saving business config never reached Better Stack.
 */
export async function patchTenantConfig(updates: TenantConfigPatch): Promise<void> {
  await apiPatch('v1-tenant-config', updates)
}

// ─── The signed-in CLIENT's own appointments (v1-widget) ───────────────────────
//
// The client surface talks to v1-widget, not the operator APIs: that function
// holds the tenant's operator token server-side, resolves the caller from their
// Supabase JWT and scopes every read and write to THAT customer. So these calls
// carry the session token and the tenant SLUG, and nothing here ever names a
// customer id — the server decides whose records these are, and a client asking
// for somebody else's booking gets booking_not_found.

/** How a session stands for money — decided server-side by `_shared/client-bookings.ts`. */
export type ClientPayment = 'paid' | 'package' | 'part_paid' | 'unpaid' | 'free'

export interface ClientBooking {
  id: string
  status: BookingStatus
  /** Tenant-local naive 'YYYY-MM-DDTHH:mm'. */
  start: string
  end: string
  price: number | null
  service: { id: string | null; name: string | null }
  // Optional so the dialog still renders against a server that predates them.
  duration_minutes?: number | null
  balance_due?: number
  payment?: ClientPayment
  /** Staff on the booking — never a machine or a room. */
  practitioner?: string | null
}

export interface ClientBookingsResponse {
  timezone: string
  currency: string
  bookings: ClientBooking[]
  /** Upcoming appointments in all; more than `bookings.length` means the list is a first page. */
  total?: number | null
  customer?: { first_name: string | null }
  business?: { name: string; address: string | null; phone: string | null }
  cancellation_policy?: string | null
}

async function widgetFetch(
  slug: string,
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<unknown> {
  const session = await supabase.auth.getSession()
  const token = session.data.session?.access_token
  const sep = path.includes('?') ? '&' : '?'
  const response = await fetch(
    `${supabaseUrl}/functions/v1/v1-widget/${path}${sep}tenant=${encodeURIComponent(slug)}`,
    {
      method: init.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    },
  )
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const err = (data as { error?: { message?: string; reason?: string } })?.error
    throw new Error(err?.message ?? err?.reason ?? `Request failed (${response.status})`)
  }
  return data
}

/** The client's own bookings. status 'booked' (default), 'all', or one status. */
export function listMyBookings(slug: string, status?: string): Promise<ClientBookingsResponse> {
  return widgetFetch(slug, `bookings${status ? `?status=${encodeURIComponent(status)}` : ''}`) as Promise<ClientBookingsResponse>
}

export function cancelMyBooking(slug: string, bookingId: string): Promise<unknown> {
  return widgetFetch(slug, `bookings/${bookingId}/cancel`, { method: 'PATCH' })
}

/** Moves the booking — same id, and its payment and any package credit move with it. */
export function rescheduleMyBooking(slug: string, bookingId: string, start: string): Promise<unknown> {
  return widgetFetch(slug, `bookings/${bookingId}/reschedule`, { method: 'PATCH', body: { start } })
}

// ─── The public package shop (v1-widget) ───────────────────────────────────────
//
// Same function and the same reasoning as the client surface above, one step
// more public: these two calls are made by somebody who may have no account at
// all, arriving from a texted link or a QR code. widgetFetch sends the session
// token only when there IS one, which is exactly the behaviour both doors need —
// a signed-in client is resolved server-side and never asked who they are.

export interface PublicPackage {
  id: string
  name: string
  price: number
  currency: string
  sessions_total: number
  validity_days: number | null
  /** Treatment names it covers. Empty with covers_everything true = unrestricted. */
  covers: string[]
  covers_everything: boolean
}

export interface PublicPackagesResponse {
  business: {
    name: string
    currency: string
    brand_primary_color: string | null
    brand_logo_url: string | null
    brand_tagline: string | null
  }
  /** False when the business has no card gateway — the page must say so, not fail at the last tap. */
  purchase_available: boolean
  packages: PublicPackage[]
}

export function listPurchasablePackages(slug: string): Promise<PublicPackagesResponse> {
  return widgetFetch(slug, 'packages') as Promise<PublicPackagesResponse>
}

/**
 * Turn a shared link into a card checkout. Returns the URL to send the browser
 * to — the gateway's own hosted page, so no card detail ever reaches this app.
 * A signed-in client passes no identity at all; the server resolves them.
 */
export function startPackagePurchase(
  slug: string,
  input: { package_id: string; name?: string; phone?: string; email?: string },
): Promise<{ redirect_url: string }> {
  return widgetFetch(slug, 'purchase', { method: 'POST', body: input }) as Promise<{ redirect_url: string }>
}

export interface ClientSlot {
  start: string
  end: string
}

/** Open slots for one service — the public widget read, used by the reschedule picker. */
export async function listSlotsForService(
  slug: string,
  serviceId: string,
  from: string,
  to: string,
): Promise<ClientSlot[]> {
  const params = new URLSearchParams({ service_id: serviceId, from, to })
  const res = await widgetFetch(slug, `availability?${params.toString()}`) as { available_slots?: ClientSlot[] }
  return res.available_slots ?? []
}

// ─── Setup API calls ───────────────────────────────────────────────────────────

// The five verb helpers below were five copies of the same twelve lines. They now
// share one, which is also the single place a client-side API failure is
// reported: a 5xx or an unreachable endpoint. 4xx is left alone deliberately —
// see reportApiFailure for why.
//
// The thrown error keeps its exact previous shape — `new Error(JSON.stringify(data))`
// — because lib/apiError.ts unwraps that JSON envelope and every caller depends
// on it.
async function request(method: string, path: string, body?: unknown): Promise<unknown> {
  const session = await supabase.auth.getSession()
  const jwt = session.data.session?.access_token

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${jwt ?? ''}`,
    ...tenantHeaders(),
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let response: Response
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  } catch (err) {
    // The request never landed — offline, DNS, CORS, a dead function. Invisible
    // from the server by definition, so this is the only place it can be seen.
    reportApiFailure({ path, method, detail: err })
    throw err
  }

  const data = await response.json()
  if (!response.ok) {
    reportApiFailure({ path, method, status: response.status })
    throw new Error(JSON.stringify(data))
  }
  return data
}

const apiPost = (path: string, body: unknown) => request('POST', path, body)
const apiPatch = (path: string, body: unknown) => request('PATCH', path, body)

// v1-availability-config uses PUT for replace-all semantics (business hours and
// resource schedules are set as a whole week, not appended to).
const apiPut = (path: string, body: unknown) => request('PUT', path, body)
const apiGet = (path: string) => request('GET', path)
const apiDelete = (path: string) => request('DELETE', path)

// ─── Bookings / Resources API (operator "today" calendar) ───────────────────────
// Read-only helpers for the default day-view calendar. The transform into
// CalendarProps lives in lib/calendar.ts.

export interface BookingRecord {
  id: string
  customer_id: string
  service_id: string
  resource_ids: string[] | null
  start: string
  end: string
  status: string
  price: number | null
  amount_paid: number | null
  payment_due: boolean | null
  package_id?: string | null
  // A package credit has been spent on this session. amount_paid stays 0 on
  // these, so this is the only way to tell a credit-covered booking from an
  // unpaid one.
  package_debited?: boolean | null
  /**
   * Attendance, as tenant-local naive datetimes. Null/absent on the whole
   * existing history — arrival was never captured before 2026-08-17 — which
   * reads correctly as "not arrived". Derive the visible state with
   * `attendanceState()` in lib/render.ts, never by testing one field.
   */
  checked_in_at?: string | null
  checked_out_at?: string | null
  /** The staff resource who delivered the session — named on its service receipt. */
  delivered_by_resource_id?: string | null
  services: { name: string; display_order?: number } | null
  customers: { id: string; phone: string | null; name: string | null } | null
}

export interface ResourceRecord {
  id: string
  name: string
  type?: string
  display_order?: number
  /**
   * Absent means active — callers that never ask for retired resources can ignore
   * this, and a payload from before the column existed still reads as in-service.
   */
  is_active?: boolean
}

export interface BookingsResponse {
  bookings: BookingRecord[]
  returned: number
  total: number | null
}

export async function listBookings(opts?: {
  from?: string
  to?: string
  status?: string
  limit?: number
  offset?: number
  customer_id?: string
  service_id?: string
  resource_id?: string
}): Promise<Page<BookingRecord> & { timezone?: string }> {
  const res = await apiGet(`v1-bookings${qs({
    from: opts?.from,
    to: opts?.to,
    status: opts?.status,
    customer_id: opts?.customer_id,
    service_id: opts?.service_id,
    resource_id: opts?.resource_id,
    limit: opts?.limit,
    offset: opts?.offset,
  })}`) as Record<string, unknown>
  return { ...toPage<BookingRecord>(res, 'bookings'), timezone: res?.timezone as string | undefined }
}

/**
 * Retired resources are hidden unless asked for. Pass `includeInactive` for any
 * HISTORICAL view — a past booking still names the machine it ran on, and without
 * it that tile loses its label. Pickers and rosters want the default.
 */
export async function listResources(
  opts: PageOpts & { includeInactive?: boolean } = {},
): Promise<Page<ResourceRecord>> {
  const res = await apiGet(`v1-resources${qs({
    include_inactive: opts.includeInactive ? 'true' : undefined,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<ResourceRecord>(res, 'resources')
}

// ─── Booking writes outside chat ───────────────────────────────────────────────
// A short, deliberately closed list. Everything here is either a single-verb
// transition on one booking (check in, settle, issue a document) or one of the
// two named decisions the payment dialog offers — never a typed value, because
// as soon as an action needs a number or a sentence (how much, a refund reason,
// a price correction) it belongs in chat, where the operator can be asked.
//
// `createBooking` (below, with the availability query) is the one write that
// takes three inputs. It was added 2026-08-10 for calendar click-to-book, and it
// holds the same line: each input is a CHOICE from a server-supplied list
// (which customer, which service, which real open slot), never something the
// operator can mistype. Price, duration and the machine are all decided
// server-side.

/** The customer's packages, each annotated with why it can (or cannot) pay for a booking. */
export interface SettlePackageOption {
  customer_package_id: string
  package_id: string
  name: string
  /**
   * Booking-scoped: for a POOLED package the whole pool, for a PER-SERVICE one
   * (per_service true) the judged service's OWN allowance — which is the number
   * that decides whether this credit can pay for this booking.
   */
  sessions_total: number
  sessions_used: number
  sessions_remaining: number
  /** Whole-package aggregates; equal to the above on a pooled package. */
  package_sessions_total?: number
  package_sessions_used?: number
  package_sessions_remaining?: number
  per_service?: boolean
  /**
   * How many credits THIS booking draws — the sale's FROZEN cost for the booked
   * service, not the service's current one. Every surface that quotes a cost
   * must quote this: it is what the debit will actually write, and on an older
   * sale it can legitimately differ from what the same treatment costs today.
   * Absent on a payload from before credit costs shipped; read that as 1.
   */
  credit_cost?: number
  /** The gated category's NAME, for display. Null when the package is ungated. */
  package_category?: string | null
  /** The gated category's id — what the gate actually compares. */
  package_category_id?: string | null
  purchased_at: string
  expires_at: string | null
  covers_service: boolean
  eligible: boolean
  ineligible_reason:
    | 'expired'
    | 'exhausted'
    | 'service_exhausted'
    // Credits remain, but fewer than this treatment costs. NOT the same as
    // exhausted — the package can still pay for something smaller — and the
    // copy must keep them apart, or a package visibly holding credits reads as
    // broken when it refuses one booking.
    | 'insufficient_credits'
    | 'service_not_covered'
    // Covered by the list, but the service has since moved out of the
    // package's category, so it has stopped being chargeable against it.
    | 'category_mismatch'
    | 'voided'
    | null
}

/** Debited a credit. */
export interface SettleSuccess {
  settled: true
  booking_id: string
  paid_with: 'package'
  customer_package_id: string
  package_name: string
  sessions_remaining: number
  sessions_total: number
  price: number
  amount_paid: number
  balance_due: number
}

/**
 * Nothing was charged — a normal outcome, not a failure. `packages` carries the
 * full picture so the caller can hand the decision to the operator's AI without
 * a second round trip.
 */
export interface SettleDeclined {
  settled: false
  reason:
    | 'no_eligible_package'         // they hold packages, none can cover this
    | 'no_packages'                 // they hold none at all
    | 'multiple_eligible_packages'  // several could — which one is a judgement call
    | 'partially_paid'              // a credit buys a whole session, not a remainder
    | 'credit_contended'            // another request spent it first
    // Only when the caller named an explicit customer_package_id (chat/MCP —
    // the calendar's settleBooking(id) never sends one):
    | 'requested_package_not_found'   // that id is not one of this customer's packages
    | 'requested_package_not_usable'  // it is, but expired/exhausted/not covering
  message: string
  booking_id: string
  customer_id: string
  customer_name: string | null
  service_name: string | null
  start: string
  price: number
  amount_paid: number
  balance_due: number
  currency: string
  packages: SettlePackageOption[]
}

export type SettleResult = SettleSuccess | SettleDeclined

/**
 * Pay a session off one of the customer's package credits. Never takes cash.
 *
 * `customerPackageId` names WHICH credit to spend — what the payment dialog sends
 * once the operator has picked one. An explicit id is honoured or refused, never
 * substituted: if it cannot cover the session the server says so rather than
 * quietly spending a different credit. Omitting it is the auto-resolve path,
 * which only spends when the choice is forced.
 */
export function settleBooking(id: string, customerPackageId?: string): Promise<SettleResult> {
  return apiPatch(
    `v1-bookings/${id}/settle`,
    customerPackageId ? { customer_package_id: customerPackageId } : {},
  ) as Promise<SettleResult>
}

// ─── How can this session be paid for? ─────────────────────────────────────────

export type PaymentMethod = 'package_credit' | 'payment_link'
export type LinkChannel = 'email' | 'sms' | 'in_app'

/**
 * Both routes to payment for one booking, whether each can work, and which one
 * this business leads with. A pure read — it charges nothing.
 *
 * `default_payment_method` is the tenant setting; `recommended_method` is that
 * setting after being downgraded to the other method when the default cannot work
 * for THIS session (no credit applies, or no gateway is configured). Render the
 * recommendation, not the raw setting.
 */
export interface PaymentOptions {
  booking_id: string
  customer_id: string
  customer_name: string | null
  /** Optional: older responses predate it. */
  service_id?: string
  service_name: string | null
  start: string
  /** The business's IANA zone. Optional: older responses predate it. */
  timezone?: string
  status: string
  payable: boolean
  not_payable_reason: 'already_paid' | 'package_debited' | 'nothing_owed' | 'not_chargeable' | null
  price: number
  amount_paid: number
  balance_due: number
  /**
   * True when the stored price is a deliberate override, in which case the
   * balance is not a meaningful ceiling and a recorded payment may exceed it —
   * the same exception v1-payments makes. Optional: older responses predate it.
   */
  price_overridden?: boolean
  currency: string
  default_payment_method: PaymentMethod
  recommended_method: PaymentMethod | null
  package_credit: {
    available: boolean
    unavailable_reason: 'no_packages' | 'no_eligible_package' | 'partially_paid' | null
    /** Several DIFFERENT packages qualify, so the operator must pick one. */
    ambiguous: boolean
    /** The one credit spendable without asking, or null when it is a judgement call. */
    auto_spendable_customer_package_id: string | null
    eligible: SettlePackageOption[]
    /** Only packages still worth showing — live ones, and expired ones with credits left. */
    ineligible: SettlePackageOption[]
    /** Spent/expired packages withheld from the list. Optional: older responses predate it. */
    ineligible_omitted?: number
  }
  card: {
    available: boolean
    unavailable_reason: 'not_configured' | 'no_contact_details' | null
    open_link: {
      id: string
      amount: number
      currency: string
      status: string
      redirect_url: string | null
      created_at: string
    } | null
    /** Where a link would be delivered, in order. */
    channels: LinkChannel[]
    /** The gateway a NEW link goes through; absent from an older server. */
    gateway?: CardGateway | null
    /** True: a test environment — the link takes test cards only, no real money. */
    test_mode?: boolean | null
  }
}

export function getPaymentOptions(bookingId: string): Promise<PaymentOptions> {
  return apiGet(`v1-bookings/${bookingId}/payment-options`) as Promise<PaymentOptions>
}

/** One calendar tile's enrichment: birthday + the covering package's counts. */
export interface DaySheetRow {
  booking_id: string
  customer_id: string
  /** Plain calendar date, or null when no profile/dob is recorded. */
  dob: string | null
  /**
   * covered — a credit was already spent for this visit.
   * available — exactly one package would cover it (its counts are below).
   * ambiguous — several different packages qualify; counts stay null because
   *   showing any one package's numbers would be a guess.
   * none — nothing usable (includes part-paid, where a credit cannot apply).
   * not_needed — already paid, or a zero-price session.
   */
  credit_state: 'covered' | 'available' | 'ambiguous' | 'none' | 'not_needed'
  package_name: string | null
  sessions_used: number | null
  sessions_total: number | null
  /**
   * Against the ACTIVE DEFAULT intake form. null = no active default form
   * (feature not in use — the dot stays credit-only) · 'complete' · 'pending'
   * (link out, unanswered) · 'not_sent'.
   */
  intake_status: 'complete' | 'pending' | 'not_sent' | null
  /** A completed form flagged medical clearance and nobody has granted it. */
  clearance_pending: boolean
}

/**
 * The calendar tile line's day feed — one round trip for the whole day instead
 * of a customer + package read per tile. Renderer feed only: everything in it
 * is already reachable through list_bookings + get_customer + package reads.
 */
export function getDaySheet(date: string): Promise<{ date: string; rows: DaySheetRow[] }> {
  return apiGet(`v1-bookings/day-sheet?date=${encodeURIComponent(date)}`) as Promise<{
    date: string
    rows: DaySheetRow[]
  }>
}

/** What a check-in did, and — when no credit moved — why not. */
export interface CheckInResult {
  booking_id: string
  /** Unchanged by a check-in. Said explicitly so nothing infers a transition. */
  status: BookingStatus
  checked_in_at: string
  checked_out_at: string | null
  /** Did THIS call spend a credit? */
  package_debited: boolean
  /** Is the visit covered by one at all — possibly spent earlier by /settle? */
  covered_by_package: boolean
  customer_package_id: string | null
  package_name: string | null
  sessions_remaining: number | null
  sessions_total: number | null
  /**
   * Why no credit moved. `already_settled` · `nothing_to_charge` ·
   * `partially_paid` · `no_packages` · `no_eligible_package` ·
   * `multiple_eligible_packages` · `credit_contended` ·
   * `requested_package_not_found` · or an `IneligibleReason`.
   */
  package_skip_reason: string | null
  packages: SettlePackageOption[]
  price: number
  amount_paid: number
  balance_due: number
  payment_due: boolean
}

/**
 * The customer has arrived: stamps `checked_in_at` and spends the credit covering
 * this visit.
 *
 * THE STATUS DOES NOT MOVE — attendance is a timestamp, so a checked-in booking
 * is still `booked`. `customerPackageId` names the credit the operator was shown
 * before confirming; an explicit id is honoured or refused, never substituted.
 *
 * ARRIVAL IS RECORDED EVEN WHEN NO CREDIT APPLIES. Unlike `settleBooking`, a
 * missing or ambiguous credit is reported in `package_skip_reason` and the
 * check-in still succeeds — somebody walked in, and that is true regardless of
 * how the session gets paid for. Callers must SAY when a credit did not move, or
 * the operator takes cash for a session a package already covers.
 *
 * 409s when the booking is not `booked`, or when it is already checked in.
 */
export function checkInBooking(id: string, customerPackageId?: string): Promise<CheckInResult> {
  return apiPatch(
    `v1-bookings/${id}/check-in`,
    customerPackageId ? { customer_package_id: customerPackageId } : {},
  ) as Promise<CheckInResult>
}

/**
 * The session is finished: sets `completed` and stamps `checked_out_at` with the
 * actual finishing time (now, unless `finishedAt` is given as a tenant-local
 * naive datetime).
 *
 * Still spends the covering credit when nothing has yet, so a business that never
 * adopted check-in behaves exactly as it did before. `checked_in_at` is NOT
 * back-filled — checking out somebody never checked in leaves arrival unknown,
 * and stamping it with the finishing time would claim they arrived as they left.
 *
 * ONE BOOKING PER CALL. A visit with several services is several bookings, and
 * the caller loops — every existing status guard then applies per booking, and
 * one service failing cannot silently take the others with it.
 */
export function checkOutBooking(
  id: string,
  finishedAt?: string,
  opts: { deliveredBy?: string } = {},
): Promise<CheckOutResult> {
  const body: Record<string, string> = {}
  if (finishedAt) body.finished_at = finishedAt
  if (opts.deliveredBy) body.delivered_by = opts.deliveredBy
  return apiPatch(`v1-bookings/${id}/complete`, body) as Promise<CheckOutResult>
}

/**
 * What happened to a session's SERVICE receipt (for a health fund / insurance claim). `issued:false` is
 * ordinary — `block` says why (not paid yet, nobody with a provider number…).
 */
export interface ServiceReceiptOutcome {
  issued: boolean
  minted?: boolean
  document_number?: string
  sent?: boolean
  recipient?: string | null
  send_error?: string | null
  send_skipped?: 'not_requested' | 'no_recipient' | 'email_unconfigured' | null
  block?: string
  message?: string
  error?: boolean
}

export interface CheckOutResult {
  id: string
  status: string
  delivered_by_resource_id?: string | null
  service_receipt?: ServiceReceiptOutcome | null
}

/**
 * Record who delivered a session (`null` clears it). On a checked-out, paid
 * session this issues its service receipt, or reissues one naming someone else.
 */
export function setBookingDeliveredBy(
  id: string,
  resourceId: string | null,
): Promise<BookingRecord & { service_receipt?: ServiceReceiptOutcome | null }> {
  return apiPatch(`v1-bookings/${id}`, { delivered_by: resourceId }) as Promise<BookingRecord & { service_receipt?: ServiceReceiptOutcome | null }>
}

/**
 * Cancel the appointment. The customer TOLD US they are not coming — that is what
 * separates this from `did_not_show`, and the API acts on the difference: cancel
 * increments `customers.cancellation_count` and stamps `last_cancellation_at`,
 * a no-show deliberately does neither.
 *
 * `reason` is optional and free text, stored on the booking. It stays optional on
 * purpose: cancelling remains a one-tap action, and the dialog never demands an
 * explanation before letting the operator free the slot.
 *
 * Rejected with 409 on anything that is not `booked`, and on a booking that is
 * already CHECKED IN — somebody standing in the building has not cancelled.
 */
export function cancelBooking(id: string, reason?: string): Promise<unknown> {
  return apiPatch(`v1-bookings/${id}/cancel`, reason ? { reason } : {})
}

/**
 * MOVES the appointment to another time — the SAME booking, a new time.
 *
 * It keeps its id, and therefore its payments, its invoices, its package debit
 * and its booking-linked notes. Until 2026-08-24 this minted a replacement row
 * and marked the original `rescheduled`, which reset a paid session to unpaid and
 * stranded any credit already spent; the response id is now always the id you
 * passed in, so a caller must not expect a new one.
 *
 * `start` is a tenant-local naive 'YYYY-MM-DDTHH:mm'. The service is reused and
 * the machine re-picked server-side, so this cannot change WHICH treatment —
 * that is a cancel plus a new booking, and the caller must not imply otherwise.
 *
 * `reason` is optional free text, stored on the booking. It describes the LAST
 * move: moving again without one clears it rather than leaving the old
 * explanation attached to a time it was never about.
 *
 * 409s on a non-`booked` booking, on one already checked in, and with
 * `slot_unavailable` when the new time is not really free.
 */
export function rescheduleBooking(id: string, start: string, reason?: string): Promise<unknown> {
  return apiPatch(`v1-bookings/${id}/reschedule`, { start, ...(reason ? { reason } : {}) })
}

// ─── Intake forms ────────────────────────────────────────────────────────────

/** One block of an intake form document — vocabulary owned by _shared/intake-doc.ts. */
export interface IntakeBlock {
  id: string
  type: 'heading' | 'paragraph' | 'text' | 'textarea' | 'yes_no' | 'number' | 'select' | 'multi_select' | 'consent'
  label: string
  required?: boolean
  /** number only: what the value is measured in. DISPLAY only — the answer is the bare number. */
  unit?: string
  /** number only: inclusive bounds. */
  min?: number
  max?: number
  /** yes_no only: answering YES flags the submission for medical clearance. */
  clearance_when_yes?: boolean
  /** multi_select only: ticking ANY option flags the submission for medical clearance. */
  clearance_when_any?: boolean
  /** select / multi_select. */
  options?: string[]
  /** multi_select only: wording of the optional free-text follow-up shown once anything is ticked. */
  note_prompt?: string
}

/** The multi_select answer: ticked options, plus the note when the block has a note_prompt. */
export interface MultiSelectAnswer {
  selected: string[]
  note?: string
}

export interface IntakeDocument {
  blocks: IntakeBlock[]
}

/**
 * Block id → answer. yes_no: 'yes'|'no' · number: a number · select: one of
 * options · multi_select: MultiSelectAnswer · consent: true · text/textarea: a
 * string. Shared with treatment cards, which use the same vocabulary.
 */
export type IntakeAnswers = Record<string, unknown>

/**
 * The email (and optional text) that carries a form to a customer. It belongs
 * to the FORM, because what somebody is asked to do differs per form — the
 * questions capture details, the invite says what to bring and by when. Owned
 * by _shared/intake-invite.ts: the document is the campaign email vocabulary
 * trimmed to heading/text/button/divider/spacer, and it must contain
 * {formLink}. A blank `sms` sends the generic line rather than nothing.
 */
export interface IntakeInvite {
  invite_subject: string
  invite_document: EmailDocument
  invite_sms: string | null
}

export interface IntakeForm extends IntakeInvite {
  id: string
  name: string
  document: IntakeDocument
  version: number
  status: 'draft' | 'active' | 'archived'
  is_default: boolean
  created_at: string
  updated_at: string
}

export interface IntakeSubmission {
  id: string
  customer_id: string
  form_id: string
  form_version: number
  status: 'not_sent' | 'sent' | 'completed'
  token: string
  answers: Record<string, unknown> | null
  completed_by: string | null
  sent_at: string | null
  completed_at: string | null
  clearance_required: boolean
  clearance_status: 'not_required' | 'pending' | 'granted'
  clearance_granted_by: string | null
  clearance_granted_at: string | null
  booking_id: string | null
  created_at: string
  /** Embedded on list/detail reads. */
  customers?: { id: string; name: string | null } | null
  intake_forms?: { id: string; name: string } | null
  /** Detail read only — the document AS SENT, which answers resolve against. */
  form_snapshot?: IntakeDocument
}

export async function listIntakeForms(opts: PageOpts & { includeArchived?: boolean } = {}): Promise<Page<IntakeForm>> {
  const res = await apiGet(
    `v1-intake${qs({ include_archived: opts.includeArchived ? 'true' : undefined, limit: opts.limit, offset: opts.offset })}`,
  )
  return toPage<IntakeForm>(res, 'forms')
}

export async function getIntakeForm(id: string): Promise<IntakeForm> {
  const res = await apiGet(`v1-intake/${id}`) as { form: IntakeForm }
  return res.form
}

/** The invite is REQUIRED — the API refuses a create without one. */
export async function createIntakeForm(body: IntakeInvite & {
  name: string
  document: IntakeDocument
  status?: 'draft' | 'active'
  is_default?: boolean
}): Promise<IntakeForm> {
  const res = await apiPost('v1-intake', body) as { form: IntakeForm }
  return res.form
}

export async function updateIntakeForm(
  id: string,
  patch: Partial<IntakeInvite> & {
    name?: string
    document?: IntakeDocument
    status?: 'draft' | 'active' | 'archived'
    is_default?: boolean
  },
): Promise<IntakeForm> {
  const res = await apiPatch(`v1-intake/${id}`, patch) as { form: IntakeForm }
  return res.form
}

export interface IntakeInviteVocabulary {
  variables: CampaignVariable[]
  blocks: EmailBlockType[]
  starter: { subject: string; document: EmailDocument; sms: string }
}

/**
 * The markers and blocks an invite may use, read from the API rather than
 * hardcoded — the reminder TemplateEditor rule: a screen offering a marker the
 * deployed backend calls illegal invites an invite that cannot be saved.
 */
export async function getIntakeInviteVocabulary(): Promise<IntakeInviteVocabulary> {
  return await apiGet('v1-intake/variables') as IntakeInviteVocabulary
}

export interface IntakeInvitePreview {
  subject: string
  html: string
  text: string
  /** What the text message would say — the form's own, or the generic line. */
  sms: string
  unresolved: string[]
}

/** Rendered by the SAME code that sends, so what is approved here is what lands. */
export async function previewIntakeInvite(
  body: IntakeInvite & { form_name?: string },
): Promise<IntakeInvitePreview> {
  const res = await apiPost('v1-intake/preview', body) as { preview: IntakeInvitePreview }
  return res.preview
}

/** Refused (409, form_has_submissions) once anything references it — archive instead. */
export function deleteIntakeForm(id: string): Promise<unknown> {
  return apiDelete(`v1-intake/${id}`)
}

// ─── Treatment cards ─────────────────────────────────────────────────────────
//
// What the OPERATOR records about a session that happened — machine settings,
// areas worked, how the client responded. The same block vocabulary as an
// intake form (IntakeBlock), narrowed by _shared/treatment-card.ts: no consent
// block and no medical clearance, because a card is written about a session
// that has already been delivered.
//
// OPERATOR-ONLY. There is no public route and no customer-facing surface — the
// difference from an intake form, which a client fills in themselves.
//
// A COMPLETED card is a CustomerNote, so it is written through
// addCustomerNote({ treatment_card_id, answers }) and read back with the rest
// of the customer's history. There is no separate record type here on purpose.

export interface TreatmentCard {
  id: string
  name: string
  document: IntakeDocument
  version: number
  status: 'draft' | 'active' | 'archived'
  is_default: boolean
  created_at: string
  updated_at: string
}

export async function listTreatmentCards(
  opts: PageOpts & { includeArchived?: boolean } = {},
): Promise<Page<TreatmentCard>> {
  const res = await apiGet(
    `v1-treatment-cards${qs({
      include_archived: opts.includeArchived ? 'true' : undefined,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  )
  return toPage<TreatmentCard>(res, 'cards')
}

export async function getTreatmentCard(id: string): Promise<TreatmentCard> {
  const res = await apiGet(`v1-treatment-cards/${id}`) as { card: TreatmentCard }
  return res.card
}

export async function createTreatmentCard(body: {
  name: string
  document: IntakeDocument
  status?: 'draft' | 'active'
  is_default?: boolean
}): Promise<TreatmentCard> {
  const res = await apiPost('v1-treatment-cards', body) as { card: TreatmentCard }
  return res.card
}

export async function updateTreatmentCard(
  id: string,
  patch: {
    name?: string
    document?: IntakeDocument
    status?: 'draft' | 'active' | 'archived'
    is_default?: boolean
  },
): Promise<TreatmentCard> {
  const res = await apiPatch(`v1-treatment-cards/${id}`, patch) as { card: TreatmentCard }
  return res.card
}

/** Refused (409, card_has_records) once anything has been filled in — archive instead. */
export function deleteTreatmentCard(id: string): Promise<unknown> {
  return apiDelete(`v1-treatment-cards/${id}`)
}

export interface TreatmentCardLast {
  card: TreatmentCard
  /** The last card of this kind recorded for this client, or null for a first session. */
  last: {
    id: string
    booking_id: string | null
    note: string
    answers: IntakeAnswers
    card_version: number
    created_at: string
  } | null
  /**
   * What today's card OPENS WITH — last time's values, minus anything the card
   * would no longer accept. Computed server-side so the web and the AI cannot
   * carry different things forward; never re-derive it here.
   */
  prefill: IntakeAnswers
}

/** One round trip for the booking sheet: the card, the last one, and the prefill. */
export async function getLastTreatmentCard(
  cardId: string,
  customerId: string,
): Promise<TreatmentCardLast> {
  return await apiGet(
    `v1-treatment-cards/${cardId}/last${qs({ customer_id: customerId })}`,
  ) as TreatmentCardLast
}

export interface PackagePurchaseLink {
  payment_link: { id: string; status: string; amount: number; currency: string; redirect_url: string | null }
  package?: { id: string; name: string; sessions_total: number }
  created: boolean
  sent_via: string[]
  emailed_to: string | null
  email_error: string | null
  texted_to: string | null
  sms_error: string | null
}

/**
 * Send the customer a card link that BUYS a package: the credits are minted by
 * settlement when the payment lands — never before. The amount is the package
 * definition's price, read server-side; one open purchase per (customer,
 * package), so asking again re-sends the SAME link.
 */
export function requestPackagePurchaseLink(customerId: string, packageId: string): Promise<PackagePurchaseLink> {
  return apiPost('v1-payment-links/packages', {
    customer_id: customerId,
    package_id: packageId,
  }) as Promise<PackagePurchaseLink>
}

export interface IntakeSendResult {
  submission: IntakeSubmission
  form: { id: string; name: string }
  created: boolean
  link: string
  sent_via: string[]
  delivery_errors: Record<string, string>
}

/**
 * Send (or re-send) a form. One OPEN submission per (customer, form): sending
 * twice re-sends the same link — `created` says which happened. `deliver:false`
 * mints/returns the link without messaging anyone (Copy link / complete now).
 */
export function sendIntakeForm(body: {
  customer_id: string
  form_id?: string
  // When no form_id is given, the form applicable to this service is used (its
  // own attached form, else the active default) — the same form the auto-send
  // would pick, so a manual re-send from the booking sheet matches it.
  service_id?: string
  booking_id?: string
  deliver?: boolean
}): Promise<IntakeSendResult> {
  return apiPost('v1-intake/send', body) as Promise<IntakeSendResult>
}

export async function listIntakeSubmissions(
  opts: PageOpts & { customerId?: string; status?: string; clearance?: string } = {},
): Promise<Page<IntakeSubmission>> {
  const res = await apiGet(
    `v1-intake/submissions${qs({
      customer_id: opts.customerId,
      status: opts.status,
      clearance: opts.clearance,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  )
  return toPage<IntakeSubmission>(res, 'submissions')
}

export async function getIntakeSubmission(id: string): Promise<IntakeSubmission> {
  const res = await apiGet(`v1-intake/submissions/${id}`) as { submission: IntakeSubmission }
  return res.submission
}

/** The human decision that a clearance-flagged answer has been looked at. */
export async function grantMedicalClearance(submissionId: string): Promise<IntakeSubmission> {
  const res = await apiPatch(`v1-intake/submissions/${submissionId}/clearance`, { granted: true }) as {
    submission: IntakeSubmission
  }
  return res.submission
}

/**
 * They never arrived and never told us. Deliberately does NOT increment
 * `cancellation_count` and does NOT debit a package credit — the slot was
 * consumed and any balance stays owed.
 *
 * Never a substitute for cancel, and an agent must not use either as a
 * catch-all: the distinction is the whole signal, and only the operator knows
 * which happened.
 */
export function markDidNotShow(id: string): Promise<unknown> {
  return apiPatch(`v1-bookings/${id}/did-not-show`, {})
}

// ─── Tax documents (invoices, receipts & adjustment notes) ──────────────────────

export type InvoiceDocumentType = 'invoice' | 'receipt' | 'adjustment_note' | 'service_receipt'

export interface InvoiceRecord {
  id: string
  /** The subject: a booking, a package sale, or (rarely) both — never neither. */
  booking_id: string | null
  customer_package_id: string | null
  customer_id: string
  /** The ledger event that minted it — a receipt's payment, an adjustment note's refund. */
  payment_id: string | null
  refund_id: string | null
  document_number: string
  document_type: InvoiceDocumentType
  /** 'Tax Invoice' | 'Tax Invoice / Receipt' | 'Invoice' | 'Receipt' | 'Adjustment Note' … */
  title?: string
  replaces_invoice_id: string | null
  issued_at: string
  subtotal: number
  gst_amount: number
  total: number
  amount_paid: number
  balance_due: number
  currency: string
  sent_to: string | null
  sent_at: string | null
  send_error: string | null
}

export interface IssueInvoiceResult {
  invoice: InvoiceRecord
  /** A number was spent on a brand-new document. */
  minted: boolean
  /** The frozen original went out again, byte for byte — nothing had moved. */
  reused: boolean
  sent: boolean
  send_error: string | null
  booking_id?: string
  customer_package_id?: string
  customer: { id: string; name: string | null; email: string | null }
  message: string
}

/**
 * Issue and email the document for one booking, on demand. WHICH document is
 * decided server-side from payment state — paid (in money or by a package
 * credit) sends a receipt, anything owing sends an invoice — so this stays a
 * single verb with no arguments, which is what keeps it inside the calendar
 * quick-action rule.
 *
 * Documents are IMMUTABLE and every payment already mints its own receipt.
 * Calling this re-sends the frozen latest document when nothing has moved, and
 * mints a NEW number (chained via replaces_invoice_id) when the money has —
 * never a second copy of the same position, and never an edit.
 */
export function issueBookingDocument(bookingId: string): Promise<IssueInvoiceResult> {
  return apiPost(`v1-invoices/bookings/${bookingId}`, {}) as Promise<IssueInvoiceResult>
}

/**
 * Email a session's SERVICE receipt (health fund / insurance claims) — normally sent automatically
 * once it is delivered and paid; an unchanged one goes out marked DUPLICATE. `healthFund`
 * records the client's fund first (null clears it), so only that fund's number prints.
 */
export function sendServiceReceipt(
  bookingId: string,
  opts: { healthFund?: string | null } = {},
): Promise<IssueInvoiceResult> {
  const body = opts.healthFund !== undefined ? { health_fund: opts.healthFund } : {}
  return apiPost(`v1-invoices/bookings/${bookingId}/service-receipt`, body) as Promise<IssueInvoiceResult>
}

/** Issue and email the document for one package sale, on demand — same contract as bookings. */
export function issueSaleDocument(saleId: string): Promise<IssueInvoiceResult> {
  return apiPost(`v1-invoices/package-sales/${saleId}`, {}) as Promise<IssueInvoiceResult>
}

/** Re-send an existing document, byte for byte from its frozen snapshot. */
export function resendDocument(invoiceId: string): Promise<{ invoice: InvoiceRecord; sent: boolean; send_error: string | null; message: string }> {
  return apiPost(`v1-invoices/${invoiceId}/send`, {}) as Promise<{ invoice: InvoiceRecord; sent: boolean; send_error: string | null; message: string }>
}

/** Documents filtered by whatever the caller knows — customer, sale, booking or payment. */
export async function listDocuments(
  filters: {
    customer_id?: string
    customer_package_id?: string
    booking_id?: string
    payment_id?: string
    document_type?: InvoiceDocumentType
  },
  opts: PageOpts = {},
): Promise<Page<InvoiceRecord>> {
  const res = await apiGet(`v1-invoices${qs({ ...filters, limit: opts.limit, offset: opts.offset })}`)
  return toPage<InvoiceRecord>(res, 'invoices')
}

// ─── Card payment links ────────────────────────────────────────────────────────

export interface PaymentIntentRecord {
  id: string
  customer_id: string
  booking_id: string | null
  kind: 'session' | 'package'
  amount: number
  currency: string
  status: 'created' | 'pending' | 'paid' | 'failed' | 'cancelled' | 'expired'
  payment_link_id: string | null
  gateway_payment_id: string | null
  gateway_status: string | null
  redirect_url: string | null
  merchant_reference: string
  payment_id: string | null
  error_message: string | null
  created_at: string
  updated_at: string
  /** Who owes the money and for what — joined server-side so the list can name
   *  the person and the session/package rather than showing a reference token. */
  customer_name?: string | null
  customer_phone?: string | null
  service_name?: string | null
  package_name?: string | null
}

export interface ChannelDelivery {
  channel: LinkChannel
  sent: boolean
  /** Email address or phone number; null for in-app. */
  to: string | null
  error: string | null
}

export interface RequestPaymentResult {
  payment_link: PaymentIntentRecord
  /** False when an open link already existed and was returned instead. */
  created: boolean
  /** For showing on screen to a customer standing at the counter. */
  qr_code_base64?: string | null
  /** Channels the link actually went out on. Empty means nobody was told. */
  sent_via?: LinkChannel[]
  /** Per-channel outcome, including the ones that could not be used and why. */
  delivery?: ChannelDelivery[] | null
  emailed_to?: string | null
  email_error?: string | null
  texted_to?: string | null
  sms_error?: string | null
  detail?: string
}

/**
 * Asks the customer to pay for one session by card.
 *
 * No arguments beyond the booking: the amount is the outstanding balance and the
 * server decides it — the operator is never asked how much. The link goes out by
 * email, SMS and in the customer's in-app thread, each degrading independently.
 * Calling it twice returns the SAME open link rather than charging twice.
 */
export function requestPaymentLink(bookingId: string): Promise<RequestPaymentResult> {
  return apiPost(`v1-payment-links/bookings/${bookingId}`, {}) as Promise<RequestPaymentResult>
}

/** Human summary of where a link went, for a toast. */
export function describeDelivery(result: RequestPaymentResult): string | null {
  const LABEL: Record<LinkChannel, string> = { email: 'email', sms: 'text', in_app: 'the app' }
  const sent = result.sent_via ?? (result.emailed_to ? (['email'] as LinkChannel[]) : [])
  if (!sent.length) return null
  const names = sent.map((c) => LABEL[c])
  if (names.length === 1) return `Sent by ${names[0]}.`
  return `Sent by ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}.`
}

/** Re-sends the email for a link that is still open. */
export function resendPaymentLink(intentId: string): Promise<{ sent: boolean; emailed_to: string | null }> {
  return apiPost(`v1-payment-links/${intentId}/send`, {}) as Promise<{ sent: boolean; emailed_to: string | null }>
}

/** Cancels an open link, at the gateway first, then here. */
export function cancelPaymentLink(intentId: string): Promise<{ payment_link: PaymentIntentRecord }> {
  return apiPost(`v1-payment-links/${intentId}/cancel`, {}) as Promise<{ payment_link: PaymentIntentRecord }>
}

/** Every payment request, newest first. Filterable by status. */
export async function listPaymentLinks(
  opts: { status?: string; customerId?: string; limit?: number; offset?: number } = {},
): Promise<Page<PaymentIntentRecord>> {
  const res = await apiGet(`v1-payment-links${qs({
    status: opts.status,
    customer_id: opts.customerId,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<PaymentIntentRecord>(res, 'payment_links')
}

/**
 * Payment requests raised against one booking, newest first.
 *
 * Returns a Page like every other list helper even though the endpoint is a
 * per-booking sub-list rather than a paged route. Consistency is the point: a
 * caller (and the test mock, which stubs every export with a paged envelope)
 * expects `.items`, and a lone helper returning a bare array is what made
 * BookingDetail throw `.find is not a function`.
 */
export async function listBookingPaymentLinks(bookingId: string): Promise<Page<PaymentIntentRecord>> {
  const res = await apiGet(`v1-payment-links/bookings/${bookingId}`)
  return toPage<PaymentIntentRecord>(res, 'payment_links')
}

// ─── Memberships ───────────────────────────────────────────────────────────────

export interface MembershipRecord {
  id: string
  customer_id: string
  package_id: string
  name: string
  amount: number
  currency: string
  interval_unit: 'week' | 'month'
  interval_count: number
  status: 'pending_setup' | 'active' | 'past_due' | 'cancelled' | 'ended'
  anchor_payment_id: string | null
  next_charge_at: string | null
  last_charged_at: string | null
  failed_attempts: number
  last_error: string | null
  started_at: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  created_at: string
  updated_at: string
  /** Joined server-side on list reads — a plan row must name WHO holds it. */
  customer_name?: string | null
  customer_phone?: string | null
}

export async function listMemberships(
  opts: { status?: string; customerId?: string; limit?: number; offset?: number } = {},
): Promise<Page<MembershipRecord>> {
  const res = await apiGet(`v1-memberships${qs({
    status: opts.status,
    customer_id: opts.customerId,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<MembershipRecord>(res, 'memberships')
}

/**
 * Start a recurring plan. Born `pending_setup`: the customer is emailed a link to
 * put a card on file, and it activates only when that first payment lands — we
 * own the schedule, so that initial payment id is the only durable handle on the
 * stored card.
 */
export function createMembership(body: {
  customer_id: string
  package_id: string
  interval?: string
}): Promise<MembershipRecord> {
  return apiPost('v1-memberships', body) as Promise<MembershipRecord>
}

/** Never retroactive — credits already paid for stay. Refunding a period is a
 *  separate, explicit decision. */
export function cancelMembership(id: string, reason?: string): Promise<MembershipRecord> {
  return apiPost(`v1-memberships/${id}/cancel`, { reason: reason ?? null }) as Promise<MembershipRecord>
}

/** Re-attempt a charge that failed. Dunning stops after three and waits for a
 *  human rather than silently cancelling somebody's plan over an expired card. */
export function retryMembershipCharge(id: string): Promise<MembershipRecord> {
  return apiPost(`v1-memberships/${id}/retry`, {}) as Promise<MembershipRecord>
}

/** Documents already issued against one booking, newest first. */
export async function listBookingDocuments(
  bookingId: string,
  opts: PageOpts = {},
): Promise<Page<InvoiceRecord>> {
  const res = await apiGet(`v1-invoices/bookings/${bookingId}${qs({ limit: opts.limit, offset: opts.offset })}`)
  return toPage<InvoiceRecord>(res, 'invoices')
}

/**
 * The rendered document, straight from its frozen snapshot. Fetched with the
 * session JWT rather than linked, because the endpoint needs an Authorization
 * header — the caller turns this into a blob URL or an iframe srcdoc.
 */
export async function getDocumentHtml(invoiceId: string): Promise<string> {
  const session = await supabase.auth.getSession()
  const jwt = session.data.session?.access_token
  const response = await fetch(`${supabaseUrl}/functions/v1/v1-invoices/${invoiceId}?format=html`, {
    headers: { Authorization: `Bearer ${jwt ?? ''}`, ...tenantHeaders() },
  })
  if (!response.ok) throw new Error(await response.text())
  return response.text()
}

// ─── Conversations API (operator inbox + customer thread poll) ──────────────────

export type ConversationChannel = 'web' | 'sms' | 'whatsapp'
export type ConversationHandler = 'ai' | 'human'
export type MessageSenderType = 'customer' | 'ai' | 'staff' | 'system' | 'automation'

/** How a message was delivered. Email is outbound-only, so it appears on a
 *  message but never on a conversation. */
export type MessageChannel = ConversationChannel | 'email'

/**
 * Which part of the system produced a message — the axis the inbox filters comms
 * on. Mirrors CommsSource in supabase/functions/_shared/conversation.ts and the
 * messages.source CHECK constraint in both tenant DDL copies. See /audit Pass 8.
 */
export type CommsSource =
  | 'chat'
  | 'sequence'
  | 'reminder'
  | 'invoice'
  | 'payment_link'
  | 'welcome'
  | 'staff'
  | 'system'
  | 'campaign'
  | 'intake'

export const COMMS_SOURCE_LABELS: Record<CommsSource, string> = {
  chat: 'Chat',
  sequence: 'Campaign step',
  reminder: 'Reminder',
  invoice: 'Tax document',
  payment_link: 'Payment request',
  welcome: 'Welcome',
  staff: 'Team reply',
  system: 'System',
  campaign: 'Campaign',
  intake: 'Intake form',
}

/**
 * The inbox's type filter. Mirrors INBOX_CATEGORIES in
 * supabase/functions/_shared/inbox-categories.ts — the server owns what each one
 * matches; this only names them. See /audit Pass 8.
 */
export type InboxCategory = 'replies' | 'reactions' | 'reminders' | 'marketing' | 'billing' | 'onboarding'

export const INBOX_CATEGORY_LABELS: Record<InboxCategory, string> = {
  replies: 'Replies',
  reactions: 'Reactions',
  reminders: 'Reminders',
  marketing: 'Marketing',
  billing: 'Payments & invoices',
  onboarding: 'Welcome & forms',
}

export type ReplyChannel = 'sms' | 'whatsapp' | 'email' | 'web'

export interface ReplyChannelOption {
  channel: ReplyChannel
  available: boolean
  reason:
    | 'subscription_required'
    | 'twilio_not_connected' | 'email_not_configured'
    | 'customer_phone_missing' | 'customer_email_missing' | 'whatsapp_not_started'
    | null
  address: string | null
}

export interface ReplyOptions {
  options: ReplyChannelOption[]
  default_channel: ReplyChannel
}

export interface MessageAttachment {
  storage_path?: string
  mime_type?: string
  name?: string
  caption?: string
  signed_url?: string
}

export interface ThreadMessage {
  id: string
  sender_type: MessageSenderType
  sender_label: string
  kind: string
  source: CommsSource
  channel: MessageChannel
  body: string
  parts: ResponsePart[] | null
  attachments: MessageAttachment[] | null
  delivery_status: string
  delivery_error: string | null
  /** The emoji, when kind is 'reaction'. */
  reaction?: string | null
  in_reply_to?: string | null
  /** The message this one answers or reacts to. */
  reply_to?: {
    id: string
    sender_type: MessageSenderType
    sender_label: string
    source: CommsSource
    channel: MessageChannel
    body: string
  } | null
  created_at: string
}

export interface ConversationSummary {
  id: string
  customer_id: string
  customer_name: string
  customer_phone: string | null
  status: 'open' | 'resolved'
  handler: ConversationHandler
  needs_response: boolean
  channel: ConversationChannel
  escalation_reason: string | null
  assigned_to: string | null
  last_message_at: string
  last_customer_message_at: string | null
  resolved_at: string | null
  created_at: string
  last_message_preview: string
  last_message_sender: string | null
  /** Comms type of the most recent message — lets the inbox row say whether the
   *  last contact was a chat reply or an automated campaign send. */
  last_message_source: CommsSource | null
  /** 'reaction' when the latest message is a tapback. */
  last_message_kind?: string | null
  last_message_reaction?: string | null
  /** When the latest message is a customer reply: the source of what it answered. */
  last_message_reply_to_source?: CommsSource | null
}

export interface InboxResponse {
  conversations: ConversationSummary[]
  count: number
  /** Counted across the whole tenant, not just this page — drives the nav badge. */
  needs_response_count: number
  timezone?: string
  total?: number | null
  limit?: number
  offset?: number
  has_more?: boolean
}

export interface ConversationDetail {
  // deno-lint-ignore no-explicit-any
  conversation: Record<string, any>
  messages: ThreadMessage[]
  /** Which channels a reply can go out on. Absent from an older API. */
  reply_options?: ReplyOptions
}

export interface CustomerThreadResponse {
  conversation: { id: string; status: string; handler: ConversationHandler; needs_response: boolean } | null
  messages: ThreadMessage[]
}

export function listConversations(
  opts?: PageOpts & {
    status?: string
    needsResponse?: boolean
    handler?: string
    customerId?: string
    /** Narrows to threads containing at least one message of this comms type. */
    source?: CommsSource
    /** Narrows to one kind of activity — see INBOX_CATEGORY_LABELS. */
    category?: InboxCategory
  },
): Promise<InboxResponse> {
  return apiGet(`v1-conversations${qs({
    status: opts?.status,
    needs_response: opts?.needsResponse ? 'true' : undefined,
    handler: opts?.handler,
    customer_id: opts?.customerId,
    source: opts?.source,
    category: opts?.category,
    limit: opts?.limit,
    offset: opts?.offset,
  })}`) as Promise<InboxResponse>
}

/**
 * One page of a thread. Paged NEWEST-first: offset 0 is the most recent page and
 * has_more means older messages exist. Messages within a page are oldest→newest.
 */
export function getConversation(
  id: string,
  opts: PageOpts & { replyTo?: string | null } = {},
): Promise<ConversationDetail> {
  return apiGet(`v1-conversations/${id}${qs({ limit: opts.limit, offset: opts.offset, reply_to: opts.replyTo ?? undefined })}`) as Promise<ConversationDetail>
}

export interface ReplyResult {
  ok: boolean
  sent_via: ReplyChannel
  delivery_status: 'sent' | 'pending' | 'failed' | 'skipped'
  delivery_error: string | null
}

export function replyToConversation(
  id: string,
  body: string,
  opts: { channel?: ReplyChannel; inReplyTo?: string | null; subject?: string } = {},
): Promise<ReplyResult> {
  return apiPost(`v1-conversations/${id}/reply`, {
    body,
    ...(opts.channel ? { channel: opts.channel } : {}),
    ...(opts.inReplyTo ? { in_reply_to: opts.inReplyTo } : {}),
    ...(opts.subject ? { subject: opts.subject } : {}),
  }) as Promise<ReplyResult>
}

/** What a new message to one customer can go out on, before anything is sent. */
export interface ComposeOptions {
  customer: { id: string; name: string | null; phone: string | null; email: string | null }
  /** The open thread a new message joins; null when it would open one. */
  conversation: { id: string; channel: ConversationChannel; handler: ConversationHandler; last_message_at: string } | null
  reply_options: ReplyOptions
}

export function getComposeOptions(customerId: string): Promise<ComposeOptions> {
  return apiGet(`v1-conversations/compose${qs({ customer_id: customerId })}`) as Promise<ComposeOptions>
}

export interface MessageDelivery {
  channel: ReplyChannel
  delivery_status: 'sent' | 'pending' | 'failed' | 'skipped'
  delivery_error: string | null
  message_id: string
}

export interface MessageCustomerResult {
  ok: boolean
  conversation_id: string
  /** Only the channels it actually went out on. */
  sent_via: ReplyChannel[]
  deliveries: MessageDelivery[]
}

/**
 * Messages a customer who has not written in, one message per channel. All or
 * nothing: a channel that cannot reach them refuses the lot (`channel_unavailable`).
 */
export function messageCustomer(
  customerId: string,
  body: string,
  opts: { channels?: ReplyChannel[]; subject?: string } = {},
): Promise<MessageCustomerResult> {
  return apiPost('v1-conversations', {
    customer_id: customerId,
    body,
    ...(opts.channels?.length ? { channels: opts.channels } : {}),
    ...(opts.subject ? { subject: opts.subject } : {}),
  }) as Promise<MessageCustomerResult>
}

export function setConversationState(
  id: string,
  updates: { handler?: ConversationHandler; needs_response?: boolean; assigned_to?: string; status?: 'resolved' },
): Promise<unknown> {
  return apiPatch(`v1-conversations/${id}`, updates)
}

export function getMyThread(): Promise<CustomerThreadResponse> {
  return apiGet('v1-conversations/me') as Promise<CustomerThreadResponse>
}

// Full history across ALL of the signed-in customer's conversations (the /me poll
// only covers the latest thread). Paginated newest-first.
export interface HistoryMessage extends ThreadMessage {
  conversation_id: string
}

export interface CustomerHistoryResponse {
  messages: HistoryMessage[]
  returned: number
  total: number | null
  limit: number
  offset: number
}

export function getMyHistory(opts?: { limit?: number; offset?: number }): Promise<CustomerHistoryResponse> {
  const params = new URLSearchParams()
  params.set('limit', String(opts?.limit ?? 50))
  params.set('offset', String(opts?.offset ?? 0))
  return apiGet(`v1-conversations/me/history?${params.toString()}`) as Promise<CustomerHistoryResponse>
}

// ─── Setup API calls ─────────────────────────────────────────────────────────── (continued)

export const setupApi = {
  createResource: (body: unknown) => apiPost('v1-resources', body),
  listResources: () => apiGet('v1-resources'),
  createService: (body: unknown) => apiPost('v1-services', body),
  listServices: () => apiGet('v1-services'),
  // v1-availability-config replaces the whole set with PUT and routes the
  // per-resource schedule as resources/:id/schedule — the previous POST +
  // resource-schedule/:id paths matched no route and 404'd.
  setBusinessHours: (body: unknown) => apiPut('v1-availability-config/business-hours', body),
  getBusinessHours: () => apiGet('v1-availability-config/business-hours'),
  setResourceSchedule: (resourceId: string, body: unknown) =>
    apiPut(`v1-availability-config/resources/${resourceId}/schedule`, body),
  getResourceSchedule: (resourceId: string) =>
    apiGet(`v1-availability-config/resources/${resourceId}/schedule`),
  getAvailability: (serviceId: string) =>
    apiGet(`v1-availability?service_id=${serviceId}`),
  updateTenantInfo: (body: Parameters<typeof patchTenantConfig>[0]) =>
    patchTenantConfig(body),
  markSetupComplete: () =>
    patchTenantConfig({ setup_complete: true }),
}

// ─── API for the operator data screens ─────────────────────────────────────────
// Overwhelmingly READ-ONLY: mutations stay in chat (and in the MCP tools) so
// there is one write path through the AI, per the chat-first design. The writes
// that do live here are the user-granted exceptions listed in CLAUDE.md → "What
// Not to Build" — the catalogue editors, the calendar's booking and quick
// actions, and the calendar's block-time / memo dialogs. Do not add a write for
// a dataset that has no such exception.
//
// Datetimes come back tenant-LOCAL naive ('YYYY-MM-DDTHH:mm'); render them with
// the helpers in lib/format.ts, never through `new Date().toLocaleString()`.

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// ─── Paging ────────────────────────────────────────────────────────────────────
// No list endpoint is open-ended: each returns at most PAGE_MAX rows plus
// total/has_more, and paging past the first page requires explicit limit/offset.
// Screens should drive lists through useApiList so "Load more" and the
// "Showing N of M" footer stay honest.

export const PAGE_MAX = 100

export interface Page<T> {
  items: T[]
  returned: number
  total: number | null
  limit: number
  offset: number
  has_more: boolean
}

/**
 * Normalises a page envelope into `Page<T>`. Tolerates a bare array so the app
 * keeps working against an endpoint that has not been redeployed yet.
 */
function toPage<T>(payload: unknown, key: string): Page<T> {
  if (Array.isArray(payload)) {
    return {
      items: payload as T[],
      returned: payload.length,
      total: payload.length,
      limit: payload.length,
      offset: 0,
      has_more: false,
    }
  }
  const obj = (payload ?? {}) as Record<string, unknown>
  const items = Array.isArray(obj[key]) ? (obj[key] as T[]) : []
  return {
    items,
    returned: typeof obj.returned === 'number' ? obj.returned : items.length,
    total: typeof obj.total === 'number' ? obj.total : null,
    limit: typeof obj.limit === 'number' ? obj.limit : items.length,
    offset: typeof obj.offset === 'number' ? obj.offset : 0,
    has_more: obj.has_more === true,
  }
}

interface PageOpts {
  limit?: number
  offset?: number
}

/**
 * How a catalogue list is ordered and filtered.
 *
 * BOTH are server-side, and that is the point rather than an implementation
 * detail. A screen holds ONE PAGE — 100 of this tenant's 496 services — so a
 * filter or a sort applied to what has loaded answers a question about the page
 * and presents it as an answer about the business: searching for a service
 * ordered past the first page reported "no services match", and a client-side
 * "most expensive first" would name the dearest of the first hundred rows.
 *
 * The keys are declared per list because they are real database columns. Keep
 * each set in step with its endpoint's ALLOWED list (/audit Pass 8).
 */
export interface ListSortOpts {
  sort?: string
  order?: 'asc' | 'desc'
  /** Substring match, applied by the database across the whole table. */
  search?: string
}

/** `?sort=` on GET /v1-services. `display_order` is the catalogue's own order. */
export const SERVICE_SORT_KEYS = ['display_order', 'name', 'price', 'duration_minutes', 'created_at'] as const
export type ServiceSortKey = (typeof SERVICE_SORT_KEYS)[number]

/** `?sort=` on GET /v1-packages. */
export const PACKAGE_SORT_KEYS = ['created_at', 'name', 'price', 'sessions_total'] as const
export type PackageSortKey = (typeof PACKAGE_SORT_KEYS)[number]

/** `?sort=` on GET /v1-services/categories. `services` orders by how many carry it. */
export const CATEGORY_SORT_KEYS = ['display_order', 'name', 'services'] as const
export type CategorySortKey = (typeof CATEGORY_SORT_KEYS)[number]

// ── Customers ──────────────────────────────────────────────────────────────────

export interface CustomerListRow {
  id: string
  phone: string
  name: string | null
  email: string | null
  role: UserRole
  created_at: string
  // Only present with include=status.
  web_user_id?: string | null
  first_login_at?: string | null
  invite_sent_at?: string | null
  cancellation_count?: number
}

export interface CustomerListResponse {
  customers: CustomerListRow[]
  returned: number
  total: number | null
  limit: number
  offset: number
  sort: string
  order: string
}

export interface CustomerListOptions {
  /** Name / email / phone lookup. */
  search?: string
  /** Full-text search across the customer's notes. */
  q?: string
  tags?: string
  marketingConsent?: boolean
  /** Comma-separated: 'owner,staff' for the team view. */
  role?: string
  /** Pass 'status' to include login/invite columns. */
  include?: string
  /**
   * Archived people are absent by default — that is what archiving is for, and
   * it applies to name resolution too. Set this for an "including archived"
   * view; it implies `include: 'status'` so the rows can be badged.
   */
  includeArchived?: boolean
  sort?: 'created_at' | 'name' | 'email'
  order?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export async function listCustomers(opts: CustomerListOptions = {}): Promise<Page<CustomerListRow>> {
  const res = await apiGet(`v1-customers${qs({
    search: opts.search,
    q: opts.q,
    tags: opts.tags,
    marketing_consent: opts.marketingConsent ? 'true' : undefined,
    role: opts.role,
    // Asking for archived people is only useful if you can SEE which ones are
    // archived, and `status` rides on the same opt-in column set — so requesting
    // one implies the other, rather than returning rows indistinguishable from
    // live ones.
    include: opts.includeArchived ? 'status' : opts.include,
    include_archived: opts.includeArchived ? 'true' : undefined,
    sort: opts.sort,
    order: opts.order,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<CustomerListRow>(res, 'customers')
}

export interface CustomerProfile {
  id: string
  customer_id: string
  goals: string[]
  health_notes: string | null
  source: string | null
  dob: string | null
  marketing_consent: boolean
  /** The fund they claim with; set, their receipts print only its provider number. */
  health_fund?: string | null
  updated_at: string
}

export interface CustomerDetail extends CustomerListRow {
  welcome_sent: boolean
  last_cancellation_at: string | null
  updated_at: string
  profile: CustomerProfile | null
  recent_notes: CustomerNote[]
  upcoming_bookings: { id: string; service_name: string | null; start: string; status: string }[]
  package_balances: {
    customer_package_id: string
    package_name: string | null
    sessions_total: number
    sessions_used: number
    sessions_remaining: number
    /** True = the totals are sums of per-service limits (split on the packages tab). */
    per_service?: boolean
  }[]
}

export function getCustomer(idOrPhone: string): Promise<CustomerDetail> {
  return apiGet(`v1-customers/${encodeURIComponent(idOrPhone)}`) as Promise<CustomerDetail>
}

export interface NoteArtifact {
  id: string
  note_id: string | null
  caption: string | null
  storage_path: string | null
  mime_type: string | null
  source: 'customer' | 'operator'
  recorded_at: string | null
  created_at: string
  /** Signed for 7 days — the media bucket is private. Cache via lib/mediaCache. */
  signed_url: string | null
}

export interface CustomerNote {
  id: string
  customer_id?: string
  booking_id: string | null
  note: string
  tags: string[]
  created_at: string
  /** Files linked to this note (photos, PDFs) — embedded by the notes list route. */
  artifacts?: NoteArtifact[]
  /** Present on q-search results: how this note matched the query. */
  matched_on?: 'words' | 'meaning'
  similarity?: number
  /**
   * A COMPLETED TREATMENT CARD is one of these notes — the operator's record of
   * what was delivered, as fields and values rather than prose. All four are
   * present together or all absent (customer_notes_card_chk). `note` is the
   * RENDER of `answers`, so an ordinary reader needs none of this.
   */
  treatment_card_id?: string | null
  card_version?: number | null
  /** The card AS ANSWERED — answers only mean anything against the questions asked. */
  card_snapshot?: IntakeDocument | null
  answers?: IntakeAnswers | null
}

export async function getCustomerNotes(
  id: string,
  opts: PageOpts & { tags?: string; q?: string; treatmentCardId?: string; cardsOnly?: boolean } = {},
): Promise<Page<CustomerNote>> {
  const res = await apiGet(
    `v1-customers/${encodeURIComponent(id)}/notes${qs({
      tags: opts.tags,
      q: opts.q,
      treatment_card_id: opts.treatmentCardId,
      cards_only: opts.cardsOnly ? 'true' : undefined,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  )
  return toPage<CustomerNote>(res, 'notes')
}

/**
 * Step 1 of saving a note: AI-suggested tags, nothing stored. The two-step flow
 * is a standing rule — tags are auto-generated but must be operator-confirmed
 * before anything is saved — so there is deliberately no one-shot helper here.
 */
export function previewNoteTags(customerId: string, note: string): Promise<{ suggested_tags: string[] }> {
  return apiPost(`v1-customers/${encodeURIComponent(customerId)}/notes?preview=true`, { note }) as Promise<{
    suggested_tags: string[]
  }>
}

export interface CustomerMeasurement {
  id: string
  customer_id: string
  metric: string
  value: number
  unit: string
  recorded_at: string | null
  note: string | null
  created_at: string
}

export async function getCustomerMeasurements(
  id: string,
  opts: PageOpts & { metric?: string } = {},
): Promise<Page<CustomerMeasurement>> {
  const res = await apiGet(
    `v1-customers/${encodeURIComponent(id)}/measurements${qs({ metric: opts.metric, limit: opts.limit, offset: opts.offset })}`,
  )
  return toPage<CustomerMeasurement>(res, 'measurements')
}

export function getCustomerProfile(id: string): Promise<CustomerProfile | null> {
  return apiGet(`v1-customers/${encodeURIComponent(id)}/profile`) as Promise<CustomerProfile | null>
}

export interface ProgressRecord {
  id: string
  conversation_id: string | null
  message_id: string | null
  note_id: string | null
  caption: string | null
  storage_path: string | null
  mime_type: string | null
  recorded_at: string | null
  /** 'customer' = logged via /progress in chat; 'operator' = saved from operator chat. */
  source?: 'customer' | 'operator'
  created_at: string
  /** Signed for 7 days — the media bucket is private. Cache via lib/mediaCache. */
  signed_url: string | null
}

export async function getCustomerProgress(id: string, opts: PageOpts = {}): Promise<Page<ProgressRecord>> {
  const res = await apiGet(
    `v1-customers/${encodeURIComponent(id)}/progress${qs({ limit: opts.limit, offset: opts.offset })}`,
  )
  return toPage<ProgressRecord>(res, 'records')
}

// ── Customer writes ────────────────────────────────────────────────────────────
//
// The whole v1-customers surface was read-only from the web app: not one mutating
// wrapper existed, so no screen could create a customer, fix a phone number,
// record a measurement or set marketing consent. Everything below already had a
// working endpoint and a working MCP tool.

export interface CustomerInput {
  /** Required on create; normalised server-side to E.164 for the tenant's region. */
  phone?: string
  name?: string | null
  email?: string | null
  role?: UserRole
}

export function createCustomer(body: CustomerInput): Promise<CustomerDetail> {
  return apiPost('v1-customers', body) as Promise<CustomerDetail>
}

/** `new_phone` renumbers; `phone` is only the lookup key in the path. */
export function updateCustomer(
  idOrPhone: string,
  body: Omit<CustomerInput, 'phone'> & { new_phone?: string },
): Promise<CustomerDetail> {
  return apiPatch(`v1-customers/${encodeURIComponent(idOrPhone)}`, body) as Promise<CustomerDetail>
}

/**
 * Take a person out of circulation, or bring them back.
 *
 * Never a delete: eighteen foreign keys point at a customer, invoices are tax
 * records that must reproduce for five years, and payments is the ledger the
 * revenue reports sum. An archived customer keeps all of that and simply stops
 * appearing in the lists operators and the AI work from.
 */
export function setCustomerArchived(
  idOrPhone: string,
  archived: boolean,
  reason?: string,
): Promise<CustomerDetail> {
  return apiPatch(`v1-customers/${encodeURIComponent(idOrPhone)}`, {
    status: archived ? 'archived' : 'active',
    ...(archived && reason ? { archive_reason: reason } : {}),
  }) as Promise<CustomerDetail>
}

export function sendCustomerInvite(idOrPhone: string): Promise<unknown> {
  return apiPost(`v1-customers/${encodeURIComponent(idOrPhone)}/invite`, {})
}

export interface CustomerProfileInput {
  goals?: string[]
  health_notes?: string | null
  source?: string | null
  dob?: string | null
  /** Gates every campaign and sequence send. Checked again at send time. */
  marketing_consent?: boolean
  /** null clears it — every provider number prints again. */
  health_fund?: string | null
}

/** Upsert — the endpoint is a PUT and there is exactly one profile per customer. */
export function updateCustomerProfile(
  idOrPhone: string,
  body: CustomerProfileInput,
): Promise<CustomerProfile> {
  return apiPut(`v1-customers/${encodeURIComponent(idOrPhone)}/profile`, body) as Promise<CustomerProfile>
}

export interface CustomerNoteInput {
  note?: string
  tags?: string[]
  booking_id?: string | null
  artifact_ids?: string[]
  /**
   * Recording a TREATMENT CARD. Send the card and the answers and the server
   * renders `note` from them — it is never sent, and on an existing card note
   * it is refused, because the row would then hold two accounts of one session.
   * Correcting one is `answers` alone on the PATCH.
   */
  treatment_card_id?: string
  answers?: IntakeAnswers
}

export function addCustomerNote(idOrPhone: string, body: CustomerNoteInput): Promise<CustomerNote> {
  return apiPost(`v1-customers/${encodeURIComponent(idOrPhone)}/notes`, body) as Promise<CustomerNote>
}

export function updateCustomerNote(
  idOrPhone: string,
  noteId: string,
  body: CustomerNoteInput,
): Promise<CustomerNote> {
  return apiPatch(
    `v1-customers/${encodeURIComponent(idOrPhone)}/notes/${noteId}`,
    body,
  ) as Promise<CustomerNote>
}

/** Also deletes the note's operator-uploaded files, and unlinks the customer's own. */
export async function deleteCustomerNote(idOrPhone: string, noteId: string): Promise<void> {
  await apiDelete(`v1-customers/${encodeURIComponent(idOrPhone)}/notes/${noteId}`)
}

export async function deleteNoteArtifact(
  idOrPhone: string,
  noteId: string,
  artifactId: string,
): Promise<void> {
  await apiDelete(`v1-customers/${encodeURIComponent(idOrPhone)}/notes/${noteId}/artifacts/${artifactId}`)
}

export interface MeasurementInput {
  metric?: string
  value?: number
  unit?: string
  /** Nullable by design — record the reading even when the date is unknown. */
  recorded_at?: string | null
  note?: string | null
}

export function addCustomerMeasurement(
  idOrPhone: string,
  body: MeasurementInput,
): Promise<CustomerMeasurement> {
  return apiPost(
    `v1-customers/${encodeURIComponent(idOrPhone)}/measurements`,
    body,
  ) as Promise<CustomerMeasurement>
}

export function updateCustomerMeasurement(
  idOrPhone: string,
  measurementId: string,
  body: MeasurementInput,
): Promise<CustomerMeasurement> {
  return apiPatch(
    `v1-customers/${encodeURIComponent(idOrPhone)}/measurements/${measurementId}`,
    body,
  ) as Promise<CustomerMeasurement>
}

/** Hard delete — nothing references a measurement, and a wrong number on a chart
 *  an operator shows the customer is worse than a gap. */
export async function deleteCustomerMeasurement(
  idOrPhone: string,
  measurementId: string,
): Promise<void> {
  await apiDelete(`v1-customers/${encodeURIComponent(idOrPhone)}/measurements/${measurementId}`)
}

// ── Bookings ───────────────────────────────────────────────────────────────────

export interface BookingDetail extends BookingRecord {
  package_id: string | null
  /**
   * Set ONLY on bookings minted by the pre-2026-08-24 supersede model, where a
   * reschedule created a replacement row and marked the original `rescheduled`.
   * Nothing writes it now that a move happens in place, but the chains already in
   * the data must keep resolving.
   */
  previous_booking_id: string | null
  /** Why it was LAST moved, when the operator gave a reason. Free text. */
  reschedule_reason: string | null
  price_overridden: boolean
  override_reason: string | null
  created_at: string
  updated_at: string
  services: { name: string; duration_minutes?: number; display_order?: number } | null
}

export function getBooking(id: string): Promise<BookingDetail> {
  return apiGet(`v1-bookings/${id}`) as Promise<BookingDetail>
}

export interface ReminderLogResponse {
  booking_id: string
  booking_start: string
  reminders: { id: string; offset_minutes: number; channel: string; sent_at: string }[]
  returned: number
}

export function getBookingReminders(id: string): Promise<ReminderLogResponse> {
  return apiGet(`v1-bookings/${id}/reminders`) as Promise<ReminderLogResponse>
}

export interface ReminderLogRow {
  id: string
  booking_id: string
  offset_minutes: number
  channel: string
  sent_at: string
  bookings: {
    id: string
    start: string
    status: string
    services: { name: string } | null
    customers: { id: string; name: string | null } | null
  } | null
}

/** Tenant-wide reminder delivery log (newest first) — the cron's audit trail. */
export async function listReminderLog(
  opts: PageOpts & { channel?: string } = {},
): Promise<Page<ReminderLogRow>> {
  const res = await apiGet(`v1-bookings/reminders${qs({
    channel: opts.channel,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<ReminderLogRow>(res, 'reminders')
}

// ── Open slots (availability query) ────────────────────────────────────────────

export interface OpenSlot { start: string; end: string }

export interface OpenSlotsResponse {
  service: { id: string; name: string; duration_minutes: number; price: number; currency: string }
  timezone: string
  available_slots: OpenSlot[]
  has_more: boolean
}

// The same open-slot computation the AI's check_availability tool runs.
// from/to are tenant-local naive datetimes; limit=0 returns the whole window.
// A day with nothing free comes back as 404 no_availability — normalised to
// null here so screens can treat "fully booked" as data, not a failure.
export async function getOpenSlots(opts: {
  serviceId: string
  from: string
  to: string
}): Promise<OpenSlotsResponse | null> {
  try {
    return await apiGet(`v1-availability${qs({
      service_id: opts.serviceId,
      from: opts.from,
      to: opts.to,
      limit: 0,
    })}`) as OpenSlotsResponse
  } catch (err) {
    if (err instanceof Error) {
      try {
        const parsed = JSON.parse(err.message) as { error?: { reason?: string } }
        if (parsed.error?.reason === 'no_availability') return null
      } catch { /* message was not the JSON error envelope — fall through */ }
    }
    throw err
  }
}

// ── Creating a booking (calendar click-to-book) ────────────────────────────────

export interface CreatedBooking {
  id: string
  status: string
  customer: { phone: string | null; name: string | null } | null
  service: { id: string; name: string; duration_minutes: number } | null
  start: string
  end: string
  /** The machines the SERVER picked. Resources are always auto-selected. */
  resources: { id: string; name: string }[] | null
  price: number | null
  amount_paid: number | null
  payment_due: boolean | null
}

/**
 * POST /v1-bookings — the availability check and the write are one atomic
 * transaction server-side, so this never needs a "still free?" call first.
 *
 * `start` is a tenant-local naive 'YYYY-MM-DDTHH:mm' (the platform's inbound
 * convention). `customer_id` rather than phone: the operator picked a real
 * customer record, and an operator does not know phone numbers.
 *
 * Deliberately NO resource_ids parameter. Resources are auto-selected — a
 * Critical Rule, and the endpoint accepts no such field. A caller that wants a
 * particular machine cannot have one, and must not imply otherwise.
 *
 * Throws with the JSON error envelope as the message (see `request`), so callers
 * read `slot_unavailable` / `service_not_found` off it and re-offer real slots.
 */
export async function createBooking(input: {
  customer_id: string
  service_id: string
  start: string
}): Promise<CreatedBooking> {
  return apiPost('v1-bookings', {
    customer_id: input.customer_id,
    service_id: input.service_id,
    start: input.start,
  }) as Promise<CreatedBooking>
}

// ── Payments & refunds ─────────────────────────────────────────────────────────

export interface PaymentRow {
  id: string
  /** Null for money with no session — a package sale or subscription charge. */
  booking_id: string | null
  /** Set when the money belongs to a package sale. */
  customer_package_id: string | null
  amount: number
  payment_method: string
  reference: string | null
  paid_at: string
  booking_start: string | null
  booking_status: string | null
  customer_id: string | null
  customer_name: string | null
  customer_phone: string | null
  service_name: string | null
  /** The package a sale-linked payment paid for. */
  package_name: string | null
  /** 'services' = treatments sold individually. Optional: older responses predate it. */
  package_kind?: 'package' | 'services' | null
}

export interface RefundRow {
  id: string
  booking_id: string | null
  customer_package_id: string | null
  amount: number
  refund_type: string
  reason: string | null
  refunded_at: string
  booking_start: string | null
  booking_status: string | null
  customer_id: string | null
  customer_name: string | null
  customer_phone: string | null
  service_name: string | null
  package_name: string | null
  package_kind?: 'package' | 'services' | null
}

interface LedgerOptions {
  bookingId?: string
  customerId?: string
  paymentMethod?: string
  refundType?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

export async function listPayments(opts: LedgerOptions = {}): Promise<Page<PaymentRow>> {
  const res = await apiGet(`v1-payments${qs({
    booking_id: opts.bookingId,
    customer_id: opts.customerId,
    payment_method: opts.paymentMethod,
    from: opts.from,
    to: opts.to,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<PaymentRow>(res, 'payments')
}

// Recording money that arrived OUTSIDE this app — a card taken on the gateway
// terminal, cash over the counter, a bank transfer. Nothing is charged here and
// no payment_intent is created: this is bookkeeping, and it is deliberately a
// different fact from the gateway confirming a capture through the webhook.
//
// Both routes are booking-scoped, so the caller must know which session the money
// is for. The server owns every limit — it refuses more than the balance due,
// refuses anything on a package-settled session, and refuses a refund larger than
// what was actually paid.

export interface RecordPaymentInput {
  amount: number
  /** See lib/paymentMethods — generic by design, never a provider's name. */
  payment_method: import('./paymentMethods').PaymentMethodValue
  /** Receipt or transaction number from wherever the money was actually taken. */
  reference?: string | null
  /** Tenant-local 'YYYY-MM-DD' the money was taken, when not today — see lib/backdating. */
  paid_at?: string | null
}

/**
 * What the auto-issued tax document did, on every money write. EVERY payment
 * mints and emails its own receipt and every refund its own adjustment note —
 * but a document problem never fails the money write behind it, so this
 * reports the outcome instead of the route throwing.
 */
export interface PaymentDocumentSummary {
  issued: boolean
  /** Present when issued=false — e.g. 'setup_required' (no ABN yet). */
  reason?: string
  id?: string
  document_number?: string
  document_type?: InvoiceDocumentType
  title?: string
  sent?: boolean
  send_error?: string | null
  recipient?: string | null
  send_skipped?: 'not_requested' | 'no_recipient' | 'email_unconfigured' | null
}

export interface PaymentResult {
  booking_id: string
  price: number
  amount_paid: number
  balance_due: number
  payment_complete: boolean
  payments: PaymentRow[]
  document?: PaymentDocumentSummary | null
}

export function recordPayment(bookingId: string, body: RecordPaymentInput): Promise<PaymentResult> {
  return apiPost(`v1-payments/bookings/${bookingId}/payments`, body) as Promise<PaymentResult>
}

export interface RefundResult {
  booking_id: string
  refund_type: 'payment' | 'credit'
  amount_refunded: number
  amount_paid_after_refund: number
  balance_due: number
  reason: string | null
  document?: PaymentDocumentSummary | null
}

export function issueRefund(
  bookingId: string,
  body: { amount: number; reason?: string | null },
): Promise<RefundResult> {
  return apiPost(`v1-payments/bookings/${bookingId}/refunds`, body) as Promise<RefundResult>
}

export async function listRefunds(opts: LedgerOptions = {}): Promise<Page<RefundRow>> {
  const res = await apiGet(`v1-payments/refunds${qs({
    booking_id: opts.bookingId,
    customer_id: opts.customerId,
    refund_type: opts.refundType,
    from: opts.from,
    to: opts.to,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<RefundRow>(res, 'refunds')
}

export interface BookingLedger {
  booking_id: string
  price: number
  amount_paid: number
  balance_due: number
  payments?: { id: string; amount: number; payment_method: string; reference: string | null; paid_at: string }[]
  refunds?: { id: string; amount: number; refund_type: string; reason: string | null; refunded_at: string }[]
  total_paid?: number
  total_refunded?: number
  returned: number
}

export function getBookingPayments(bookingId: string): Promise<BookingLedger> {
  return apiGet(`v1-payments/bookings/${bookingId}/payments`) as Promise<BookingLedger>
}

export function getBookingRefunds(bookingId: string): Promise<BookingLedger> {
  return apiGet(`v1-payments/bookings/${bookingId}/refunds`) as Promise<BookingLedger>
}

// ── Packages ───────────────────────────────────────────────────────────────────

export interface PackageDefinition {
  id: string
  name: string
  /** On a per-service definition (service_allocations set) this is the SUM of
   *  the per-service counts, maintained server-side. */
  sessions_total: number
  price: number
  currency: string
  service_ids: string[]
  /** Per-service session limits ({"<service_id>": sessions}); null = pooled. */
  service_allocations?: Record<string, number> | null
  // Named server-side. The client cannot resolve service_ids itself — one page of
  // services is 100 rows and this tenant has 496, so anything past the first page
  // rendered as a bare uuid. `name` is null for a service no longer in the catalogue.
  // `sessions` is that service's own limit on a per-service package, null on a pooled one.
  services?: Array<{
    id: string
    name: string | null
    sessions?: number | null
    /** What one booking of it draws from the credits. Null when the service is
     *  gone from the catalogue — there is no live cost, and a 1 would be a
     *  guess dressed as a fact. */
    credit_cost?: number | null
    /** The service's CURRENT category. */
    category?: string | null
    /** False = this covered service has moved OUT of the package's category and
     *  has stopped being chargeable against it. Surfaced rather than hidden:
     *  the alternative is the operator discovering it at a check-in. */
    in_category?: boolean
  }>
  /**
   * Scopes the credits to ONE service category; null = unscoped. Judged LIVE
   * against each service's current category, so a service moved out stops being
   * chargeable while the credits and everything still in scope keep working.
   *
   * `category` is the NAME (for display) and `category_id` is the gate. They are
   * one composite foreign key into `service_categories`, so they cannot
   * disagree, and renaming a category cascades to both.
   */
  category?: string | null
  category_id?: string | null
  validity_days: number | null
  gst_treatment: 'taxable' | 'gst_free'
  is_active: boolean
  created_at: string
}

export async function listPackages(
  opts: PageOpts & ListSortOpts & { includeInactive?: boolean } = {},
): Promise<Page<PackageDefinition>> {
  const res = await apiGet(`v1-packages${qs({
    include_inactive: opts.includeInactive ? 'true' : undefined,
    search: opts.search,
    sort: opts.sort,
    order: opts.order,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<PackageDefinition>(res, 'packages')
}

export function getPackage(id: string): Promise<PackageDefinition> {
  return apiGet(`v1-packages/${id}`) as Promise<PackageDefinition>
}

/** One covered service's own balance on a per-service sale, named server-side. */
export interface ServiceBalance {
  service_id: string
  service_name: string | null
  sessions_total: number
  sessions_used: number
  sessions_remaining: number
}

export interface PackageAssignment {
  id: string
  customer_id: string
  package_id: string
  sessions_used: number
  /** SNAPSHOT of the definition's per-service limits at sale time; null = pooled. */
  service_allocations?: Record<string, number> | null
  service_usage?: Record<string, number>
  /** Per-service balances derived from the snapshot; null on a pooled sale. */
  service_balances?: ServiceBalance[] | null
  payment_reference: string | null
  expires_at: string | null
  purchased_at: string
  package_name: string | null
  /** 'services' = treatments sold individually rather than a package off the shelf. */
  package_kind?: 'package' | 'services'
  price: number | null
  currency: string | null
  /** Snapshot-derived (sum of allocations) on a per-service sale. */
  sessions_total: number
  sessions_remaining: number
  is_expired: boolean
  customer_name: string | null
  customer_phone: string | null
  /**
   * Money LINKED to this sale (payments minus refunds), derived server-side.
   * Momence-era sales carry their money unlinked, so 0 here means "not tracked",
   * never "unpaid" — render positive statements only, no unpaid badge on a zero.
   */
  amount_paid: number
  /** price − amount_paid, floored at 0. Null when the definition has no price. */
  balance_due: number | null
}

export async function listPackageAssignments(
  opts: PageOpts & { packageId?: string; customerId?: string } = {},
): Promise<Page<PackageAssignment>> {
  const res = await apiGet(`v1-packages/assignments${qs({
    package_id: opts.packageId,
    customer_id: opts.customerId,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<PackageAssignment>(res, 'assignments')
}

/** One customer's package holdings (includes sessions_remaining + is_expired). */
export async function getCustomerPackages(
  idOrPhone: string,
  opts: PageOpts = {},
): Promise<Page<PackageAssignment>> {
  const res = await apiGet(
    `v1-packages/customers/${encodeURIComponent(idOrPhone)}/packages${qs({ limit: opts.limit, offset: opts.offset })}`,
  )
  return toPage<PackageAssignment>(res, 'packages')
}

/** Sell a package to a customer. */
export function assignPackage(
  idOrPhone: string,
  body: { package_id: string; payment_reference?: string | null; purchased_at?: string | null },
): Promise<PackageAssignment> {
  return apiPost(
    `v1-packages/customers/${encodeURIComponent(idOrPhone)}/packages`,
    body,
  ) as Promise<PackageAssignment>
}

/** One treatment line of a services sale. */
export interface ServiceSaleLine {
  service_id: string
  quantity: number
  /** The agreed price per session — sent only when it is not the list price (a discount). */
  unit_price?: number
}

/**
 * Sell treatments individually, ahead of time. Priced server-side from the
 * catalogue; the result is a sale like a package's (its id is the
 * customer_package_id the money step and settleBooking take). Never expires.
 */
export function sellServices(
  idOrPhone: string,
  body: { services: ServiceSaleLine[]; payment_reference?: string | null; purchased_at?: string | null },
): Promise<PackageAssignment> {
  return apiPost(
    `v1-packages/customers/${encodeURIComponent(idOrPhone)}/service-sales`,
    body,
  ) as Promise<PackageAssignment>
}

export interface PackageAssignmentPatch {
  /** Correcting a mis-debit on a POOLED sale. Refused above the package's total,
   *  and refused entirely on a per-service sale — send service_usage there. */
  sessions_used?: number
  /** Per-service sales: the {"<service_id>": used} counts, each capped by that
   *  service's sessions on THIS sale. The aggregate counter is derived. */
  service_usage?: Record<string, number>
  /** A bare date resolves to the END of that day, so "extend to the 31st" does
   *  not arrive already expired. `null` removes the expiry. */
  expires_at?: string | null
  payment_reference?: string | null
  status?: 'active' | 'voided'
  void_reason?: string | null
}

export function updatePackageAssignment(
  id: string,
  body: PackageAssignmentPatch,
): Promise<PackageAssignment> {
  return apiPatch(`v1-packages/assignments/${id}`, body) as Promise<PackageAssignment>
}

/**
 * Reverse a sale recorded against the wrong customer.
 *
 * Voiding, not deleting, whenever the sale has been used or a booking points at
 * it — the credits already spent have to keep resolving. The server refuses the
 * delete in exactly those cases and says so.
 */
export function voidPackageAssignment(id: string, reason?: string): Promise<PackageAssignment> {
  return updatePackageAssignment(id, { status: 'voided', void_reason: reason ?? null })
}

/** Hard delete, refused by the server unless the sale is genuinely untouched. */
export async function deletePackageAssignment(id: string): Promise<void> {
  await apiDelete(`v1-packages/assignments/${id}`)
}

// ── Money on a package sale ─────────────────────────────────────────────────────
// Same contract as the booking routes above: recording is bookkeeping for money
// taken OUTSIDE the app, the server owns every limit (never above the sale's
// balance, nothing on a voided sale, refunds capped at what was paid), and the
// ledger rows are never edited — a payment is corrected by a refund.

export interface SalePaymentResult {
  customer_package_id: string
  package_name: string | null
  price: number
  amount_paid: number
  balance_due: number
  payment_complete: boolean
  document?: PaymentDocumentSummary | null
}

export function recordSalePayment(
  customerPackageId: string,
  body: RecordPaymentInput,
): Promise<SalePaymentResult> {
  return apiPost(
    `v1-payments/package-sales/${customerPackageId}/payments`,
    body,
  ) as Promise<SalePaymentResult>
}

export interface SaleRefundResult {
  customer_package_id: string
  package_name: string | null
  amount_refunded: number
  amount_paid_after_refund: number
  balance_due: number
  reason: string | null
  document?: PaymentDocumentSummary | null
}

export function issueSaleRefund(
  customerPackageId: string,
  body: { amount: number; reason?: string | null },
): Promise<SaleRefundResult> {
  return apiPost(
    `v1-payments/package-sales/${customerPackageId}/refunds`,
    body,
  ) as Promise<SaleRefundResult>
}

export interface SalePaymentLinkResult {
  payment_link: { id: string; status: string; redirect_url: string | null; amount: number }
  created: boolean
  sent_via?: string[]
  emailed_to?: string | null
  email_error?: string | null
  texted_to?: string | null
  sms_error?: string | null
}

/**
 * Email/text the customer a hosted card link for the sale's outstanding balance.
 * The server computes the amount; asking again re-sends the same open link.
 */
export function createSalePaymentLink(customerPackageId: string): Promise<SalePaymentLinkResult> {
  return apiPost(
    `v1-payment-links/package-sales/${customerPackageId}`,
    {},
  ) as Promise<SalePaymentLinkResult>
}

// ── Catalogue ──────────────────────────────────────────────────────────────────

export interface ServiceRecord {
  id: string
  name: string
  duration_minutes: number
  price: number
  currency: string
  resource_ids: string[]
  resources_required: number | null
  category: string | null
  display_order: number
  is_active: boolean
  /**
   * false = OPERATOR-ONLY: the business books it, no customer-facing surface
   * offers it. A DIFFERENT axis from is_active — retired means nobody can book
   * it, including the operator — and the UI must keep the two apart.
   */
  customer_bookable: boolean
  /**
   * Least warning a CUSTOMER must give to book this, in minutes; null = none.
   * A THIRD axis, distinct from both flags above: the service is on sale and the
   * business can book it whenever it likes — customers just cannot book inside
   * the window.
   */
  min_notice_minutes: number | null
  /**
   * How many package CREDITS one booking of this service spends. A FOURTH axis,
   * independent of the three above: the service is on sale, bookable by anyone,
   * bookable right now — this only changes how much of a package it uses up.
   * Absent on an older payload; read that as 1, never as 0.
   */
  credit_cost?: number
  /** The category's id. `category` is its name; the two are one composite
   *  foreign key into `service_categories` and cannot disagree. */
  category_id?: string | null
  gst_treatment: 'taxable' | 'gst_free'
  /**
   * The intake form a CUSTOMER must complete before their first booking of this
   * service. null = falls back to the tenant's active default form (the seeded
   * template), so every service is always covered. Set it to give one treatment
   * its own questionnaire; the applicable form is auto-sent on a first booking.
   */
  intake_form_id: string | null
  created_at: string
}

/**
 * 457 of the first tenant's 495 services are retired Momence imports, so the API
 * hides inactive ones unless asked. Screens must surface that explicitly.
 */
export async function listServices(
  includeInactive = false,
  opts: PageOpts & ListSortOpts = {},
): Promise<Page<ServiceRecord>> {
  const res = await apiGet(`v1-services${qs({
    include_inactive: includeInactive ? 'true' : undefined,
    search: opts.search,
    sort: opts.sort,
    order: opts.order,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<ServiceRecord>(res, 'services')
}

export interface ResourceDetail extends ResourceRecord {
  buffer_minutes: number
  staff_role: string | null
  staff_email: string | null
  staff_phone: string | null
  /** Printed on invoices and receipts when set; staff resources only. */
  provider_number: string | null
  provider_qualification: string | null
  association_number: string | null
  /** One provider number per health fund; every one prints on the receipts. */
  fund_provider_numbers?: FundProviderNumber[] | null
  created_at: string
}

export async function listResourcesDetailed(
  opts: PageOpts & { includeInactive?: boolean } = {},
): Promise<Page<ResourceDetail>> {
  const res = await apiGet(`v1-resources${qs({
    include_inactive: opts.includeInactive ? 'true' : undefined,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<ResourceDetail>(res, 'resources')
}

// ── Catalogue writes ───────────────────────────────────────────────────────────
// The read API above is read-only by design; these are the exception the user
// granted on 2026-08-09 so services, packages and resources can be created,
// edited and retired without a chat turn. They write CATALOGUE DEFINITIONS only —
// never a customer record, a booking or money.
//
// There is no delete anywhere here, and that is deliberate: every one of these
// rows is referenced by history that must keep resolving (bookings point at
// services, customer_packages at packages, resource_schedule and the utilisation
// report at resources). Retiring sets is_active=false; restoring sets it back.

export function getService(id: string): Promise<ServiceRecord> {
  return apiGet(`v1-services/${id}`) as Promise<ServiceRecord>
}

export interface ServiceInput {
  name: string
  duration_minutes: number
  price: number
  resource_ids: string[]
  resources_required?: number | null
  category?: string | null
  display_order?: number
  gst_treatment?: 'taxable' | 'gst_free'
  customer_bookable?: boolean
  /** Minutes of notice a customer must give; null or 0 removes the restriction. */
  min_notice_minutes?: number | null
  /** Credits one booking spends out of a package. Minimum 1; omit for 1. */
  credit_cost?: number
  /** Intake form id, or null/'' to clear back to the tenant default template. */
  intake_form_id?: string | null
  currency?: string
}

export function createService(body: ServiceInput): Promise<ServiceRecord> {
  return apiPost('v1-services', body) as Promise<ServiceRecord>
}

export function updateService(id: string, updates: Partial<ServiceInput>): Promise<ServiceRecord> {
  return apiPatch(`v1-services/${id}`, updates) as Promise<ServiceRecord>
}

/** Retire (`false`) or restore (`true`). Never deletes — bookings reference this row. */
export function setServiceActive(id: string, isActive: boolean): Promise<ServiceRecord> {
  return apiPatch(`v1-services/${id}`, { is_active: isActive }) as Promise<ServiceRecord>
}

/**
 * A category heading the service menu is grouped under.
 *
 * Categories are NOT a table — `services.category` is free text, so the set of
 * them is whatever the catalogue happens to say. That is why this has to be
 * fetched rather than derived client-side: one page of services is 100 rows and
 * this tenant has 497, so counting them in the browser would report the
 * categories of the first page as though they were all of them.
 */
export interface ServiceCategory {
  /** The stored value, verbatim — it is what a rename must match exactly. */
  name: string
  active: number
  total: number
  /** Lowest `display_order` among the category's services. Category order is
   *  derived from it; there is no per-category order field. */
  display_order: number
}

export interface ServiceCategoryList {
  categories: ServiceCategory[]
  /** Categories matching the search — `total` stays the count of ALL of them. */
  returned: number
  total: number
  /** Services with NO category. Counted apart from the named ones, because a
   *  business can legitimately have a real category called "Other". */
  uncategorised: number
  services_scanned: number
  /** True means the catalogue is bigger than the server's scan cap and this set
   *  may be incomplete. Surface it — a short list that looks whole is how a
   *  rename misses rows. */
  truncated: boolean
}

export function listServiceCategories(
  opts: Pick<ListSortOpts, 'search'> & { sort?: CategorySortKey; order?: 'asc' | 'desc' } = {},
): Promise<ServiceCategoryList> {
  // Sorted and filtered server-side even though the whole set comes back in one
  // response, so the AI surfaces get the same view the screen does and there is
  // one definition of what "by size" means. `display_order` is the order the
  // customer sees in the booking widget — see _shared/service-categories.ts.
  return apiGet(`v1-services/categories${qs({
    search: opts.search,
    sort: opts.sort,
    order: opts.order,
  })}`) as Promise<ServiceCategoryList>
}

export interface RenameCategoryResult {
  from: string
  to: string | null
  services_updated: number
  active_updated: number
  /** The target already existed, so two categories became one. Not undone by
   *  renaming back. */
  merged: boolean
}

/**
 * Rename a category across every service carrying it — or merge two by renaming
 * one to the other's exact name. `to: null` clears the category off them.
 *
 * Retired services are updated too, deliberately: a rename that spared them
 * would leave the old spelling on retired rows, where it reappears in the
 * category list the moment anybody looks.
 */
export function renameServiceCategory(from: string, to: string | null): Promise<RenameCategoryResult> {
  return apiPost('v1-services/categories/rename', { from, to }) as Promise<RenameCategoryResult>
}

/**
 * Put a service on sale to customers (`true`) or make it operator-only (`false`).
 *
 * Not a milder retire — the service stays live and fully bookable from the
 * calendar and from chat either way. All `false` does is take it off every
 * customer-facing surface: the public widget, the customer app's catalogue, and
 * what the customer-facing assistant can see or resolve by name.
 */
export function setServiceCustomerBookable(id: string, bookable: boolean): Promise<ServiceRecord> {
  return apiPatch(`v1-services/${id}`, { customer_bookable: bookable }) as Promise<ServiceRecord>
}

export function getResource(id: string): Promise<ResourceDetail> {
  return apiGet(`v1-resources/${id}`) as Promise<ResourceDetail>
}

export interface ResourceInput {
  name: string
  type: 'room' | 'staff' | 'equipment'
  buffer_minutes?: number
  display_order?: number
  staff_role?: string | null
  staff_email?: string | null
  staff_phone?: string | null
  provider_number?: string | null
  provider_qualification?: string | null
  association_number?: string | null
  fund_provider_numbers?: FundProviderNumber[]
}

export function createResource(body: ResourceInput): Promise<ResourceDetail> {
  return apiPost('v1-resources', body) as Promise<ResourceDetail>
}

export function updateResource(id: string, updates: Partial<ResourceInput>): Promise<ResourceDetail> {
  return apiPatch(`v1-resources/${id}`, updates) as Promise<ResourceDetail>
}

/** Retire (`false`) or restore (`true`). Never deletes — schedules, time blocks
 *  and report_by_resource all reference this row. */
export function setResourceActive(id: string, isActive: boolean): Promise<ResourceDetail> {
  return apiPatch(`v1-resources/${id}`, { is_active: isActive }) as Promise<ResourceDetail>
}

export interface PackageInput {
  name: string
  /** Pooled packages only — derived (and refused if conflicting) when
   *  service_allocations is sent. */
  sessions_total?: number
  price: number
  service_ids?: string[]
  /** Per-service limits: each covered service with its own session count.
   *  service_ids and sessions_total are derived server-side. */
  service_allocations?: Array<{ service_id: string; sessions: number }> | null
  /** Scope the credits to one service category BY NAME; '' or null removes the
   *  scope. Every covered service must be in it, or the write is refused.
   *  Unlike a service, a package never creates a category — an unknown name is
   *  refused, because a gate no service satisfies is a package covering
   *  nothing. `category_id` is accepted instead and takes precedence. */
  category?: string | null
  category_id?: string | null
  validity_days?: number | null
  gst_treatment?: 'taxable' | 'gst_free'
  currency?: string
}

export function createPackage(body: PackageInput): Promise<PackageDefinition> {
  return apiPost('v1-packages', body) as Promise<PackageDefinition>
}

export function updatePackage(id: string, updates: Partial<PackageInput>): Promise<PackageDefinition> {
  return apiPatch(`v1-packages/${id}`, updates) as Promise<PackageDefinition>
}

/** Retire (`false`) or restore (`true`). Never deletes — credits already sold
 *  reference this definition and must keep resolving. */
export function setPackageActive(id: string, isActive: boolean): Promise<PackageDefinition> {
  return apiPatch(`v1-packages/${id}`, { is_active: isActive }) as Promise<PackageDefinition>
}

// ── Availability config ────────────────────────────────────────────────────────

export interface BusinessHourRow {
  id: string
  day_of_week: number
  open_time: string
  close_time: string
  created_at: string
}

export async function getBusinessHours(opts: PageOpts = {}): Promise<Page<BusinessHourRow>> {
  const res = await apiGet(`v1-availability-config/business-hours${qs({ limit: opts.limit, offset: opts.offset })}`)
  return toPage<BusinessHourRow>(res, 'hours')
}

export interface ResourceScheduleRow {
  id: string
  resource_id: string
  day_of_week: number
  start_time: string
  end_time: string
  created_at: string
}

export async function getResourceSchedule(
  resourceId: string,
  opts: PageOpts = {},
): Promise<Page<ResourceScheduleRow>> {
  const res = await apiGet(
    `v1-availability-config/resources/${resourceId}/schedule${qs({ limit: opts.limit, offset: opts.offset })}`,
  )
  return toPage<ResourceScheduleRow>(res, 'schedule')
}

export interface TimeBlockRow {
  id: string
  resource_id: string | null
  block_start: string
  block_end: string
  reason: string | null
  created_at: string
}

/**
 * `from`/`to` select closures OVERLAPPING the window, not ones starting inside
 * it — a week-long closure has to appear on every day it covers, including the
 * ones after it started.
 */
export async function listTimeBlocks(
  opts: PageOpts & { upcoming?: boolean; from?: string; to?: string } = {},
): Promise<Page<TimeBlockRow>> {
  const res = await apiGet(`v1-availability-config/time-blocks${qs({
    upcoming: opts.upcoming ? 'true' : undefined,
    from: opts.from,
    to: opts.to,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<TimeBlockRow>(res, 'time_blocks')
}

// Availability writes.
//
// Both weekly tables are saved by REPLACE-ALL: the array sent is the whole week
// from then on, and `[]` means closed every day. That is the API's semantics, not
// a convenience — a partial send silently deletes whatever it omits, so callers
// must always pass the complete set. See lib/weekSchedule.ts for the value model.

export interface BusinessHourInput {
  day_of_week: number
  open_time: string
  close_time: string
}

export function setBusinessHours(hours: BusinessHourInput[]): Promise<unknown> {
  return apiPut('v1-availability-config/business-hours', hours)
}

export interface ResourceScheduleInput {
  day_of_week: number
  start_time: string
  end_time: string
}

export function setResourceSchedule(
  resourceId: string,
  schedule: ResourceScheduleInput[],
): Promise<unknown> {
  return apiPut(`v1-availability-config/resources/${resourceId}/schedule`, schedule)
}

/** Every resource's schedule in one read — the per-resource route N+1s a list screen. */
export async function listResourceSchedules(opts: PageOpts = {}): Promise<Page<ResourceScheduleRow>> {
  const res = await apiGet(
    `v1-availability-config/resource-schedules${qs({ limit: opts.limit, offset: opts.offset })}`,
  )
  return toPage<ResourceScheduleRow>(res, 'schedule')
}

export interface TimeBlockInput {
  /** null closes the whole business for the window. */
  resource_id?: string | null
  /** Tenant-local naive `YYYY-MM-DDTHH:mm`, per the platform's API convention. */
  block_start?: string
  block_end?: string
  reason?: string | null
}

export function createTimeBlock(body: TimeBlockInput): Promise<TimeBlockRow> {
  return apiPost('v1-availability-config/time-blocks', body) as Promise<TimeBlockRow>
}

export function updateTimeBlock(id: string, body: TimeBlockInput): Promise<TimeBlockRow> {
  return apiPatch(`v1-availability-config/time-blocks/${id}`, body) as Promise<TimeBlockRow>
}

export async function deleteTimeBlock(id: string): Promise<void> {
  await apiDelete(`v1-availability-config/time-blocks/${id}`)
}

// ── Calendar memos ─────────────────────────────────────────────────────────────
// A note pinned to a point on the calendar. It does NOT hold the time — the slot
// stays bookable, which is the whole distinction from a time block. Nothing in
// the availability computation reads these.

export interface CalendarNoteRow {
  id: string
  resource_id: string | null
  /** Tenant-local naive 'YYYY-MM-DDTHH:mm'. */
  note_at: string
  note: string
  created_by: string | null
  created_at: string
  updated_at: string
}

export async function listCalendarNotes(
  opts: PageOpts & { from?: string; to?: string; resourceId?: string } = {},
): Promise<Page<CalendarNoteRow>> {
  const res = await apiGet(`v1-calendar-notes${qs({
    from: opts.from,
    to: opts.to,
    resource_id: opts.resourceId,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<CalendarNoteRow>(res, 'calendar_notes')
}

export function createCalendarNote(input: {
  note: string
  /** Tenant-local naive 'YYYY-MM-DDTHH:mm'. */
  note_at: string
  resource_id?: string | null
}): Promise<CalendarNoteRow> {
  return apiPost('v1-calendar-notes', {
    note: input.note,
    note_at: input.note_at,
    resource_id: input.resource_id ?? null,
  }) as Promise<CalendarNoteRow>
}

export function updateCalendarNote(
  id: string,
  updates: { note?: string; note_at?: string; resource_id?: string | null },
): Promise<CalendarNoteRow> {
  return apiPatch(`v1-calendar-notes/${id}`, updates) as Promise<CalendarNoteRow>
}

export function deleteCalendarNote(id: string): Promise<{ deleted: boolean }> {
  return apiDelete(`v1-calendar-notes/${id}`) as Promise<{ deleted: boolean }>
}

// ── Comms ──────────────────────────────────────────────────────────────────────

export interface PromptTemplate {
  id: string
  name: string
  channel: 'email' | 'sms'
  event_type: 'reminder' | 'marketing'
  /** Email only — the subject line, and it may itself carry {markers}. */
  subject: string | null
  body: string | null
  canva_template_id: string | null
  active: boolean
  created_at: string
  updated_at: string
  /**
   * Legacy columns from when reminders were AI-generated. Still selected by the
   * list route, written by nothing, and IGNORED by POST and PATCH — reminder text
   * is now substituted verbatim, because a model retyping the appointment time is
   * how a customer got told the wrong one. Read-only; never offer them for edit.
   */
  system_prompt?: string
  placeholders?: { name: string; description?: string }[]
}

/** The legal {markers}. The API rejects any other on save, so the editor must
 *  show this list rather than let an operator guess. */
export interface TemplateVariable {
  name: string
  description: string
  example: string
}

export async function listPromptTemplates(
  opts: PageOpts = {},
): Promise<Page<PromptTemplate> & { variables: TemplateVariable[] }> {
  const res = await apiGet(`v1-prompt-templates${qs({ limit: opts.limit, offset: opts.offset })}`)
  const page = toPage<PromptTemplate>(res, 'templates')
  const variables = (res as { variables?: TemplateVariable[] })?.variables ?? []
  return { ...page, variables }
}

export interface PromptTemplateInput {
  name?: string
  channel?: 'email' | 'sms'
  event_type?: 'reminder' | 'marketing'
  subject?: string | null
  body?: string
  canva_template_id?: string | null
  /**
   * Activating one RETIRES its siblings on the same (channel, event_type) — the
   * cron sends exactly one template per channel, and skips the channel silently
   * when none is active. So this is closer to a radio button across rows than a
   * per-row checkbox, and the screen has to say so.
   */
  active?: boolean
}

export function createPromptTemplate(body: PromptTemplateInput): Promise<PromptTemplate> {
  return apiPost('v1-prompt-templates', body) as Promise<PromptTemplate>
}

export function updatePromptTemplate(id: string, body: PromptTemplateInput): Promise<PromptTemplate> {
  return apiPatch(`v1-prompt-templates/${id}`, body) as Promise<PromptTemplate>
}

export async function deletePromptTemplate(id: string): Promise<void> {
  await apiDelete(`v1-prompt-templates/${id}`)
}

export interface NotificationSchedule {
  id: string
  name: string
  skill_name: string
  skill_params: Record<string, unknown>
  summary_prompt: string
  recurrence: string
  send_time: string
  day_of_week: number | null
  channel: string
  recipient_override: string | null
  active: boolean
  next_send_at: string | null
  last_sent_at: string | null
  chain_enabled?: boolean
  created_at: string
}

export async function listNotificationSchedules(opts: PageOpts = {}): Promise<Page<NotificationSchedule>> {
  const res = await apiGet(`v1-notifications${qs({ limit: opts.limit, offset: opts.offset })}`)
  return toPage<NotificationSchedule>(res, 'schedules')
}

export interface NotificationScheduleInput {
  name?: string
  /** One of the read skills in the cron's SKILL_API map. */
  skill_name?: string
  /** Supports {today}, {tomorrow_start}, {week_end} … resolved in tenant time. */
  skill_params?: Record<string, unknown>
  summary_prompt?: string
  recurrence?: 'daily' | 'weekly' | 'monthly'
  /** `HH:mm`, tenant-local. */
  send_time?: string
  /** 1=Mon … 7=Sun. Only meaningful for a weekly recurrence. */
  day_of_week?: number | null
  channel?: 'email' | 'sms'
  recipient_override?: string | null
  active?: boolean
}

export function createNotificationSchedule(
  body: NotificationScheduleInput,
): Promise<NotificationSchedule> {
  return apiPost('v1-notifications', body) as Promise<NotificationSchedule>
}

export function updateNotificationSchedule(
  id: string,
  body: NotificationScheduleInput,
): Promise<NotificationSchedule> {
  return apiPatch(`v1-notifications/${id}`, body) as Promise<NotificationSchedule>
}

export async function deleteNotificationSchedule(id: string): Promise<void> {
  await apiDelete(`v1-notifications/${id}`)
}

export interface NotificationLogRow {
  id: string
  schedule_id: string | null
  channel: string
  recipient: string
  subject: string | null
  sent_at: string
  error: string | null
  /** Joined server-side; null when the schedule was since deleted. */
  schedule_name?: string | null
}

export async function listNotificationLog(
  opts: PageOpts & { scheduleId?: string; channel?: string; failed?: boolean } = {},
): Promise<Page<NotificationLogRow>> {
  const res = await apiGet(`v1-notifications/log${qs({
    schedule_id: opts.scheduleId,
    channel: opts.channel,
    failed: opts.failed ? 'true' : undefined,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<NotificationLogRow>(res, 'log')
}

// ── Marketing sequences (drip campaigns) ────────────────────────────────────────
// The SECOND editable dataset outside chat and the setup wizard — an explicit
// user-granted exception to the read-only data-screens rule, scoped to sequences
// and their steps only. Granted because a drip email body is too large to author
// in a chat turn: the operator needs a real editor with the whole body in front of
// them. Enrolling a customer, pausing an enrolment and every other mutation in the
// app still go through chat.
//
// The containing rule: this editor writes SEQUENCE DEFINITIONS only. It never
// sends anything, and it cannot mutate a customer record.

export type SequenceStatus = 'draft' | 'active' | 'paused' | 'archived'
export type SequenceStartAnchor = 'immediately' | 'next_weekday' | 'specific_date'
export type SequenceTrigger = 'manual' | 'customer_created' | 'booking_completed' | 'package_assigned'
export type SequenceStepChannel = 'email' | 'sms'
export type SequenceEnrolmentStatus = 'active' | 'paused' | 'completed' | 'cancelled'
export type SequenceSendStatus = 'pending' | 'sent' | 'failed' | 'skipped'

export interface SequenceRow {
  id: string
  name: string
  description: string | null
  status: SequenceStatus
  start_anchor: SequenceStartAnchor
  start_weekday: number | null
  start_date: string | null
  trigger_event: SequenceTrigger
  trigger_service_ids: string[]
  trigger_package_ids: string[]
  require_consent: boolean
  stale_after_hours: number | null
  activated_at: string | null
  last_swept_at: string | null
  created_at: string
  updated_at: string
  step_count?: number
  enrollment_count?: number
  active_enrollment_count?: number
  sent_count?: number
}

export interface SequenceStepRow {
  id: string
  sequence_id: string
  step_order: number
  name: string
  channel: SequenceStepChannel
  subject: string | null
  body: string
  wait_days: number
  wait_hours: number
  send_at_time: string | null
  snap_to_weekday: number | null
  created_at: string
  updated_at: string
  /** Server-rendered plain-English timing, e.g. '3 days later, at 07:00'. */
  timing?: string
}

/** When each step would land for somebody enrolling right now. Recomputed on
 *  every read, so it always reflects the current step definitions. */
export interface SequenceScheduleEntry {
  step_id: string
  step_order: number
  name: string
  due_at: string
  timing?: string
}

export interface SequenceVariable {
  name: string
  description: string
  example: string
}

export interface SequenceDetailResponse {
  sequence: SequenceRow
  steps: SequenceStepRow[]
  schedule: SequenceScheduleEntry[]
  variables: SequenceVariable[]
}

export interface SequenceEnrolmentRow {
  id: string
  sequence_id: string
  customer_id: string
  status: SequenceEnrolmentStatus
  anchor_at: string
  next_step_id: string | null
  next_run_at: string | null
  enrolled_by: SequenceTrigger
  trigger_ref_id: string | null
  completed_at: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  created_at: string
  updated_at: string
  sequences?: { id: string; name: string; status: SequenceStatus } | null
  customers?: { id: string; name: string | null; email: string | null; phone: string | null } | null
}

export interface SequenceSendRow {
  id: string
  enrollment_id: string
  sequence_id: string
  step_id: string
  customer_id: string
  step_order: number
  channel: SequenceStepChannel
  status: SequenceSendStatus
  recipient: string | null
  subject: string | null
  error: string | null
  message_id: string | null
  scheduled_for: string
  sent_at: string | null
  created_at: string
  sequences?: { name: string } | null
  sequence_steps?: { name: string } | null
  customers?: { id: string; name: string | null } | null
}

export interface SequenceInput {
  name: string
  description?: string | null
  status?: SequenceStatus
  start_anchor?: SequenceStartAnchor
  start_weekday?: number | null
  start_date?: string | null
  trigger_event?: SequenceTrigger
  trigger_service_ids?: string[]
  trigger_package_ids?: string[]
  stale_after_hours?: number | null
}

export interface SequenceStepInput {
  name: string
  channel: SequenceStepChannel
  subject?: string | null
  body: string
  wait_days?: number
  wait_hours?: number
  send_at_time?: string | null
  snap_to_weekday?: number | null
}

export async function listSequences(
  opts: PageOpts & { status?: SequenceStatus; triggerEvent?: SequenceTrigger } = {},
): Promise<Page<SequenceRow>> {
  const res = await apiGet(`v1-sequences${qs({
    status: opts.status,
    trigger_event: opts.triggerEvent,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<SequenceRow>(res, 'sequences')
}

export function getSequence(id: string): Promise<SequenceDetailResponse> {
  return apiGet(`v1-sequences/${id}`) as Promise<SequenceDetailResponse>
}

export async function listSequenceEnrolments(
  opts: PageOpts & { sequenceId?: string; customerId?: string; status?: SequenceEnrolmentStatus } = {},
): Promise<Page<SequenceEnrolmentRow>> {
  const res = await apiGet(`v1-sequences/enrollments${qs({
    sequence_id: opts.sequenceId,
    customer_id: opts.customerId,
    status: opts.status,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<SequenceEnrolmentRow>(res, 'enrollments')
}

/**
 * Put one customer on a campaign. The manual trigger is the DEFAULT trigger
 * event, and it had no UI anywhere — only a chat handoff.
 *
 * The schedule is recomputed from `anchor_at` on every run, never from the last
 * send, which is what lets an edited wait re-flow the remaining steps for people
 * already mid-campaign.
 */
export function enrolCustomerInSequence(
  sequenceId: string,
  body: { customer_id: string; anchor_at?: string },
): Promise<SequenceEnrolmentRow> {
  return apiPost(`v1-sequences/${sequenceId}/enrollments`, body) as Promise<SequenceEnrolmentRow>
}

/**
 * Pause, resume or cancel one person's enrolment — "stop sending me these" is
 * the most common request this feature generates, and the screen listing those
 * people had no control for it.
 */
export function updateSequenceEnrolment(
  enrolmentId: string,
  body: { status: 'active' | 'paused' | 'cancelled' },
): Promise<SequenceEnrolmentRow> {
  return apiPatch(`v1-sequences/enrollments/${enrolmentId}`, body) as Promise<SequenceEnrolmentRow>
}

export async function listSequenceSends(
  opts: PageOpts & {
    sequenceId?: string
    customerId?: string
    status?: SequenceSendStatus
    channel?: SequenceStepChannel
  } = {},
): Promise<Page<SequenceSendRow>> {
  const res = await apiGet(`v1-sequences/sends${qs({
    sequence_id: opts.sequenceId,
    customer_id: opts.customerId,
    status: opts.status,
    channel: opts.channel,
    limit: opts.limit,
    offset: opts.offset,
  })}`)
  return toPage<SequenceSendRow>(res, 'sends')
}

export function createSequence(body: SequenceInput): Promise<SequenceDetailResponse> {
  return apiPost('v1-sequences', body) as Promise<SequenceDetailResponse>
}

export function updateSequence(id: string, updates: Partial<SequenceInput>): Promise<{ sequence: SequenceRow }> {
  return apiPatch(`v1-sequences/${id}`, updates) as Promise<{ sequence: SequenceRow }>
}

export async function deleteSequence(id: string): Promise<void> {
  await apiDelete(`v1-sequences/${id}`)
}

export function createSequenceStep(
  sequenceId: string,
  body: SequenceStepInput,
): Promise<{ step: SequenceStepRow }> {
  return apiPost(`v1-sequences/${sequenceId}/steps`, body) as Promise<{ step: SequenceStepRow }>
}

export function updateSequenceStep(
  sequenceId: string,
  stepId: string,
  updates: Partial<SequenceStepInput>,
): Promise<{ step: SequenceStepRow }> {
  return apiPatch(`v1-sequences/${sequenceId}/steps/${stepId}`, updates) as Promise<{ step: SequenceStepRow }>
}

export async function deleteSequenceStep(sequenceId: string, stepId: string): Promise<void> {
  await apiDelete(`v1-sequences/${sequenceId}/steps/${stepId}`)
}

/** Renumbers the whole chain. `stepIds` must list every step exactly once. */
export function reorderSequenceSteps(
  sequenceId: string,
  stepIds: string[],
): Promise<{ steps: SequenceStepRow[] }> {
  return apiPost(`v1-sequences/${sequenceId}/steps/reorder`, { step_ids: stepIds }) as Promise<{
    steps: SequenceStepRow[]
  }>
}

// ── Note styles & voice-notes transcription ─────────────────────────────────────
// Note styles are the ONE dataset editable from the Settings screen — an explicit
// user-granted exception (2026-08-02) to the read-only data-screens rule, scoped
// to this dataset only. Everything else still mutates through chat.

export interface NoteStyle {
  id: string
  name: string
  instructions: string
  is_default: boolean
  display_order: number
  created_at: string
  updated_at: string
}

export interface NoteStyleInput {
  name: string
  instructions: string
  is_default?: boolean
  display_order?: number
}

export async function listNoteStyles(opts: PageOpts = {}): Promise<Page<NoteStyle>> {
  const res = await apiGet(`v1-note-styles${qs({ limit: opts.limit, offset: opts.offset })}`)
  return toPage<NoteStyle>(res, 'styles')
}

export function createNoteStyle(body: NoteStyleInput): Promise<NoteStyle> {
  return apiPost('v1-note-styles', body) as Promise<NoteStyle>
}

export function updateNoteStyle(id: string, updates: Partial<NoteStyleInput>): Promise<NoteStyle> {
  return apiPatch(`v1-note-styles/${id}`, updates) as Promise<NoteStyle>
}

export async function deleteNoteStyle(id: string): Promise<void> {
  await apiDelete(`v1-note-styles/${id}`)
}

// Voice notes: the recorder uploads ~5-minute audio segments; the server runs
// AssemblyAI (speaker diarization + medical vocabulary) and returns text that
// may contain "Speaker A:" lines when more than one voice was detected.
export interface TranscribeResult {
  text: string
  speakers: string[]
  duration_seconds: number | null
}

export function transcribeAudio(audioBase64: string, mimeType: string): Promise<TranscribeResult> {
  return apiPost('v1-transcribe', { audio: audioBase64, mime_type: mimeType }) as Promise<TranscribeResult>
}

export function summariseTranscript(text: string): Promise<{ summary: string }> {
  return apiPost('v1-transcribe/summary', { text }) as Promise<{ summary: string }>
}

// ── Team (app access) ──────────────────────────────────────────────────────────

export interface OperatorRow {
  id: string
  user_email: string
  user_id: string | null
  role: string
  created_at: string
  has_signed_in: boolean
}

export async function listOperators(opts: PageOpts = {}): Promise<Page<OperatorRow>> {
  const res = await apiGet(`v1-tenant-config/operators${qs({ limit: opts.limit, offset: opts.offset })}`)
  return toPage<OperatorRow>(res, 'operators')
}

/**
 * Grant somebody access to manage this business, and email them the invitation.
 *
 * This is a different thing from adding a STAFF RECORD (a customers row with
 * role owner/staff): the staff record is who works here, this is who can sign
 * in. A tenant can legitimately have operators and no staff records — the first
 * one does.
 */
export interface OperatorInviteResult {
  operator: OperatorRow
  /**
   * `magic_link` when the address already had an auth account — they are a
   * customer here, or were invited to another tenant — so a fresh invitation
   * would 422. Worth telling the operator: the two emails look different in the
   * recipient's inbox, and "I never got an invite" is usually this.
   */
  delivery: 'invite' | 'magic_link'
  login_url: string
}

export function inviteOperator(body: { email: string; name?: string }): Promise<OperatorInviteResult> {
  return apiPost('v1-tenant-config/operators', body) as Promise<OperatorInviteResult>
}

/**
 * Revoke access. A hard delete, because the row IS the grant — a soft-deleted
 * one would only be a grant somebody has to remember to ignore.
 *
 * The server refuses the last remaining operator and refuses the owner's own
 * account. Both refusals are recoverable mistakes to make; the alternative,
 * locking every human out of a live business, is not.
 */
export async function revokeOperator(id: string): Promise<void> {
  await apiDelete(`v1-tenant-config/operators/${id}`)
}

// ── System users (API tokens for AI assistants) ────────────────────────────────

/**
 * A named, non-human operator with an assigned role, for an AI assistant that only accepts a fixed
 * Authorization header. Owner-only, and never listed with its token — only `token_prefix`.
 */
export interface SystemUserRow {
  id: string
  name: string
  role: string
  token_prefix: string
  created_by: string | null
  created_at: string
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  status: 'active' | 'revoked' | 'expired'
}

export async function listSystemUsers(): Promise<{ system_users: SystemUserRow[]; roles: string[] }> {
  return await apiGet('v1-tenant-config/system-users') as { system_users: SystemUserRow[]; roles: string[] }
}

/** Creates a system user. The response is the ONLY time `token` exists anywhere but the owner's hands. */
export function createSystemUser(body: { name: string; role: string; expires_at?: string }): Promise<{ system_user: SystemUserRow; token: string }> {
  return apiPost('v1-tenant-config/system-users', body) as Promise<{ system_user: SystemUserRow; token: string }>
}

/** Revokes a system user's token immediately. Kept as a revoked row so its past actions still have a name. */
export async function revokeSystemUser(id: string): Promise<void> {
  await apiDelete(`v1-tenant-config/system-users/${id}`)
}

// ── Card gateway (Stripe / Worldline) ──────────────────────────────────────────

export type CardGateway = 'stripe' | 'worldline'

/** The non-secret picture GET /v1-tenant-config returns to operators. Keys never leave the server. */
export interface CardGatewayState {
  card_payments_configured: boolean
  card_gateway: CardGateway | null
  card_gateways: {
    stripe: {
      connected: boolean
      account_id: string | null
      account_name: string | null
      livemode: boolean | null
      webhook_registered: boolean
      connected_at: string | null
    }
    /** false = ANZ's sandbox: links work but no real money can move. */
    worldline: { connected: boolean; livemode: boolean }
  }
}

export interface StripeConnectResult extends CardGatewayState {
  /** False: the account cannot take live charges yet (Stripe still needs details). */
  charges_enabled: boolean
  /** Set when the webhook could not be registered — payments then settle via the reconcile cron. */
  webhook_error: string | null
}

/** Owner only. Validates the key with Stripe, registers the webhook, and makes Stripe the live gateway. */
export function connectStripe(secretKey: string): Promise<StripeConnectResult> {
  return apiPost('v1-tenant-config/payment-gateway/stripe', { secret_key: secretKey }) as Promise<StripeConnectResult>
}

/** Owner only. Refused (409) while open links or memberships still depend on the account. */
export function disconnectStripe(): Promise<CardGatewayState> {
  return apiDelete('v1-tenant-config/payment-gateway/stripe') as Promise<CardGatewayState>
}

/** Owner only. Which connected gateway new links use, when both are connected. */
export function setCardGateway(use: CardGateway): Promise<CardGatewayState> {
  return apiPatch('v1-tenant-config/payment-gateway', { use }) as Promise<CardGatewayState>
}

// ── Reports ────────────────────────────────────────────────────────────────────

export interface ReportOverview {
  bookings_total: number
  bookings_booked: number
  bookings_completed: number
  bookings_cancelled: number
  bookings_did_not_show: number
  bookings_upcoming: number
  bookings_today: number
  bookings_unpaid: number
  customers_total: number
  customers_active_90d: number
  customers_new_30d: number
  revenue_collected: number
  refunds_total: number
  outstanding_balance: number
  services_active: number
  services_total: number
  resources_total: number
  packages_total: number
  package_sales_total: number
  package_credits_outstanding: number
  notes_total: number
  measurements_total: number
  conversations_open: number
  conversations_needs_response: number
}

export interface MonthlyRow {
  month: string
  bookings: number
  completed: number
  cancelled: number
  /** bookings.amount_paid bucketed by the month the service happened. */
  revenue: number
  /** payments.amount bucketed by the month cash arrived. */
  collected: number
  refunded: number
  new_customers: number
}

export interface ServiceReportRow {
  service_id: string
  service_name: string
  category: string | null
  bookings: number
  completed: number
  cancelled: number
  revenue: number
}

export interface StatusReportRow { status: string; bookings: number; revenue: number }

export interface ResourceReportRow {
  resource_id: string
  resource_name: string
  resource_type: string
  bookings: number
  booked_minutes: number
}

export interface TopCustomerRow {
  customer_id: string
  name: string | null
  phone: string
  visits: number
  spend: number
  last_visit: string | null
}

export interface DayReport {
  date: string
  bookings_total: number
  bookings_booked: number
  bookings_completed: number
  bookings_cancelled: number
  bookings_did_not_show: number
  customers_seen: number
  /** Earned on this day's sessions (bookings.amount_paid). */
  revenue: number
  /** Cash banked on this day (payments.paid_at) — not the same thing. */
  collected: number
  outstanding: number
  bookings_unpaid: number
  booked_minutes: number
  first_start: string | null
  last_end: string | null
  resources_used: number
  new_customers: number
  top_services: { service_name: string; bookings: number }[]
}

export interface ReportsResponse {
  overview: ReportOverview
  day: DayReport
  monthly: MonthlyRow[]
  by_service: ServiceReportRow[]
  by_status: StatusReportRow[]
  by_resource: ResourceReportRow[]
  top_customers: TopCustomerRow[]
  timezone: string
  currency: string
}

export function getReports(opts: { months?: number; date?: string } = {}): Promise<ReportsResponse> {
  return apiGet(`v1-reports${qs({ months: opts.months, date: opts.date })}`) as Promise<ReportsResponse>
}

/** Just the day panel — used when the date scroller moves, so the charts don't refetch. */
export async function getDayReport(date?: string): Promise<DayReport> {
  const res = await apiGet(`v1-reports/day${qs({ date })}`) as { data: DayReport }
  return res.data
}

export interface BusiestTimeRow {
  /** ISO weekday: 1 = Monday … 7 = Sunday, in the tenant timezone. */
  day_of_week: number
  hour: number
  bookings: number
}

/** Bookings histogram by weekday × hour — the one series not in the combined payload. */
export async function getBusiestTimes(): Promise<BusiestTimeRow[]> {
  const res = await apiGet('v1-reports/busiest-times') as { data?: BusiestTimeRow[] }
  // Always an array out, like listBookingPaymentLinks: the caller charts it, and
  // an undefined `data` would reach the renderer as a crash rather than "no rows".
  return res.data ?? []
}

// â”€â”€â”€ Email campaigns â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Template (what to say) + segment (who to say it to) + campaign (one sending of
// one to the other). See docs/email-campaigns-design.md.
//
// The block types below mirror _shared/email-doc.ts exactly. Adding one is a
// change in BOTH files plus a renderer in EmailBlockEditor â€” see /audit Pass 8.

export type EmailBlockType = 'heading' | 'text' | 'button' | 'image' | 'divider' | 'spacer' | 'html'
export type EmailAlignment = 'left' | 'center' | 'right'

export interface EmailBlockBase { id: string; align?: EmailAlignment }
export type EmailBlock =
  | (EmailBlockBase & { type: 'heading'; text: string; level?: 1 | 2 | 3 })
  | (EmailBlockBase & { type: 'text'; text: string })
  | (EmailBlockBase & { type: 'button'; label: string; url: string })
  // `alt` is optional and derived (from the uploaded file's name), never typed
  // by the operator â€” see the note on ImageBlock in _shared/email-doc.ts.
  | (EmailBlockBase & { type: 'image'; src: string; alt?: string | null; href?: string | null; width?: number | null })
  | (EmailBlockBase & { type: 'divider' })
  | (EmailBlockBase & { type: 'spacer'; height?: number })
  | (EmailBlockBase & { type: 'html'; html: string })

export interface EmailDocumentTheme {
  backgroundColor?: string
  contentBackgroundColor?: string
  textColor?: string
  linkColor?: string
  buttonColor?: string
  buttonTextColor?: string
  fontFamily?: string
}

export interface EmailDocument {
  version: 1
  theme?: EmailDocumentTheme
  blocks: EmailBlock[]
}

export interface CampaignVariable {
  name: string
  description: string
  example: string
  always?: boolean
}

export interface TemplatePreview {
  subject: string
  html: string
  text: string
  placeholders: string[]
  unresolved: string[]
}

// Rendered by the SAME code that sends, so what the operator approves here is
// what lands in an inbox. Called on a debounce as the builder is edited.
export function previewCampaignEmail(body: {
  document: EmailDocument
  subject?: string
  preheader?: string | null
  segment_id?: string | null
}): Promise<TemplatePreview> {
  return apiPost('v1-campaigns/preview', body) as Promise<TemplatePreview>
}

export interface UploadedTemplateImage {
  url: string
  path: string
  /** Derived from the filename, and often empty. Never asked of the operator. */
  alt: string
}

// An image block's src is always an upload. A pasted link is not checkable: it
// may resolve for the operator and 404 for every recipient, and by then the mail
// has gone. Downscale before calling this (prepareTemplateImage) â€” a phone photo
// is several MB, and nothing in a 600px email column needs that.
export function uploadTemplateImage(
  file: { name: string; mime_type: string; data: string },
): Promise<UploadedTemplateImage> {
  return apiPost('v1-campaigns/images', file) as Promise<UploadedTemplateImage>
}

// `segmentId` widens the list with whatever that audience projects.
//
// Returns a bare array, so the shared api mock in screens.smoke.test.tsx needs an
// explicit row for it — see the note beside getBusiestTimes there. The
// Array.isArray guard is not belt-and-braces: the builder maps over this on its
// first render, so a malformed body would white-screen the whole editor rather
// than showing an empty marker palette.
export async function listCampaignVariables(segmentId?: string | null): Promise<CampaignVariable[]> {
  const res = await apiGet(`v1-campaigns/variables${qs({ segment_id: segmentId ?? undefined })}`) as
    { variables?: CampaignVariable[] }
  return Array.isArray(res?.variables) ? res.variables : []
}

// â”€â”€ Segments â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export type SegmentFilterOp =
  | 'is' | 'is_not' | 'is_true' | 'is_false' | 'in' | 'not_in'
  | 'contains' | 'includes_any' | 'at_least' | 'at_most'
  | 'within_days' | 'not_within_days' | 'before' | 'after'

export interface SegmentCondition {
  field: string
  op: SegmentFilterOp
  value?: unknown
  within_days?: number
}

export interface SegmentFilter {
  match: 'all' | 'any'
  conditions: SegmentCondition[]
}

export interface SegmentFieldSpec {
  field: string
  group: 'profile' | 'bookings' | 'spend' | 'packages'
  label: string
  ops: SegmentFilterOp[]
  supports_within_days: boolean
}

export interface SegmentFieldsResponse {
  fields: SegmentFieldSpec[]
  groups: Array<{ key: string; label: string }>
  always_applied: string[]
  consent_note: string
  required_columns: string[]
}

export interface SegmentRow {
  id: string
  name: string
  description: string | null
  sql: string
  filters: SegmentFilter | null
  last_count: number | null
  last_counted_at: string | null
  created_at: string
  updated_at: string
}

export interface SegmentPreview {
  count: number
  capped: boolean
  sql: string
  description: string | null
  placeholders: string[]
  sample?: Array<Record<string, unknown>>
}

export function listSegmentFields(): Promise<SegmentFieldsResponse> {
  return apiGet('v1-segments/fields') as Promise<SegmentFieldsResponse>
}

export async function listSegments(opts: PageOpts = {}): Promise<Page<SegmentRow>> {
  const res = await apiGet(`v1-segments${qs({ limit: opts.limit, offset: opts.offset })}`)
  return toPage<SegmentRow>(res, 'segments')
}

export function getSegment(
  id: string,
  opts: { recount?: boolean } = {},
): Promise<{ segment: SegmentRow; placeholders: string[]; count?: number }> {
  return apiGet(`v1-segments/${id}${qs({ recount: opts.recount === false ? 'false' : undefined })}`) as
    Promise<{ segment: SegmentRow; placeholders: string[]; count?: number }>
}

// Compile + validate + COUNT, writing nothing. This is what the audience step
// calls on every edit, and what the AI calls before proposing a segment.
export function previewSegment(body: {
  filters?: SegmentFilter
  sql?: string
  include_sample?: boolean
}): Promise<SegmentPreview> {
  return apiPost('v1-segments/preview', body) as Promise<SegmentPreview>
}

export function createSegment(body: {
  name: string
  description?: string | null
  filters?: SegmentFilter
  sql?: string
}): Promise<{ segment: SegmentRow; count: number; placeholders: string[] }> {
  return apiPost('v1-segments', body) as Promise<{ segment: SegmentRow; count: number; placeholders: string[] }>
}

export function updateSegment(
  id: string,
  updates: { name?: string; description?: string | null; filters?: SegmentFilter; sql?: string },
): Promise<{ segment: SegmentRow; placeholders: string[] }> {
  return apiPatch(`v1-segments/${id}`, updates) as Promise<{ segment: SegmentRow; placeholders: string[] }>
}

export async function deleteSegment(id: string): Promise<void> {
  await apiDelete(`v1-segments/${id}`)
}

// â”€â”€ Campaigns â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed'
export type CampaignSendStatus = 'pending' | 'sent' | 'failed' | 'skipped'

// A CAMPAIGN OWNS ITS EMAIL (2026-09-09). There is no separate template row and
// no `template_id`: the subject, preheader and block document live here, so the
// wizard can edit the email in place. Reuse is `duplicateCampaign` — a COPY,
// which is what an operator means by a template.
export interface CampaignRow {
  id: string
  name: string
  segment_id: string | null
  status: CampaignStatus
  subject: string
  preheader: string | null
  placeholders: string[]
  require_consent: boolean
  scheduled_for: string | null
  recipient_count: number | null
  sent_count: number
  failed_count: number
  skipped_count: number
  started_at: string | null
  completed_at: string | null
  last_error: string | null
  created_at: string
  updated_at: string
  // Present on the detail route only — the list omits them because a document
  // and a rendered email are large.
  document?: EmailDocument
  html?: string
}

export interface CampaignDetail {
  campaign: CampaignRow
  segment: { id: string; name: string | null } | null
  recipient_count: number | null
  launch_check: { ok: boolean; missing: string[]; available: string[] }
  // Every reason this cannot go out yet, so the review step shows them all at
  // once instead of revealing them one refusal at a time. The same list gates
  // both sending now and scheduling.
  blockers: string[]
  text: string
}

export interface CampaignSendRow {
  id: string
  customer_id: string
  customer_name: string | null
  recipient: string | null
  subject: string | null
  status: CampaignSendStatus
  skip_reason: string | null
  error: string | null
  sent_at: string | null
  opened_at: string | null
  clicked_at: string | null
  unsubscribed_at: string | null
  open_count: number
  click_count: number
  created_at: string
}

export type CampaignEngagement = 'opened' | 'clicked' | 'unsubscribed'

export interface CampaignReport {
  campaign_id: string
  status: CampaignStatus
  sent: number
  skipped: number
  failed: number
  // Unique recipients, from the first-touch stamps. Opens are an UPPER bound:
  // mail scanners and image proxies fire the pixel too.
  opened: number
  clicked: number
  unsubscribed: number
  total_clicks: number
  links: Array<{ id: string; url: string; click_count: number }>
  skip_reasons: Record<string, number>
  note: string
}

export interface CampaignLaunchResult {
  status: CampaignStatus
  audience_size: number
  processed_this_run: number
  sent: number
  failed: number
  skipped: number
  remaining: number
  sent_count: number
  failed_count: number
  skipped_count: number
  note: string
}

export async function listCampaigns(
  opts: PageOpts & { status?: CampaignStatus } = {},
): Promise<Page<CampaignRow>> {
  const res = await apiGet(`v1-campaigns${qs({
    status: opts.status, limit: opts.limit, offset: opts.offset,
  })}`)
  return toPage<CampaignRow>(res, 'campaigns')
}

export function getCampaign(id: string): Promise<CampaignDetail> {
  return apiGet(`v1-campaigns/${id}`) as Promise<CampaignDetail>
}

// Three ways to start, all landing on the same shape: blank, a copy of another
// campaign's email (`copy_from_campaign_id` — the template capability), or an
// email supplied outright.
export function createCampaign(body: {
  name: string
  copy_from_campaign_id?: string
  subject?: string
  preheader?: string | null
  document?: EmailDocument
  segment_id?: string | null
}): Promise<{ campaign: CampaignRow }> {
  return apiPost('v1-campaigns', body) as Promise<{ campaign: CampaignRow }>
}

export function updateCampaign(
  id: string,
  updates: {
    name?: string
    subject?: string
    preheader?: string | null
    document?: EmailDocument
    segment_id?: string | null
    status?: CampaignStatus
  },
): Promise<{ campaign: CampaignRow }> {
  return apiPatch(`v1-campaigns/${id}`, updates) as Promise<{ campaign: CampaignRow }>
}

export async function deleteCampaign(id: string): Promise<void> {
  await apiDelete(`v1-campaigns/${id}`)
}

// "Start from this email" — what a template was for, kept as the one thing it
// was ever used for. The copy is INDEPENDENT (editing it cannot rewrite the
// original) and deliberately carries NO audience: a copy is a new decision about
// who to mail, and inheriting the last one is how somebody sends twice to the
// same list without noticing.
export function duplicateCampaign(id: string, name?: string): Promise<{ campaign: CampaignRow }> {
  return apiPost(`v1-campaigns/${id}/duplicate`, name ? { name } : {}) as Promise<{ campaign: CampaignRow }>
}

export async function listCampaignSends(
  campaignId: string,
  opts: PageOpts & { status?: CampaignSendStatus; engagement?: CampaignEngagement } = {},
): Promise<Page<CampaignSendRow>> {
  const res = await apiGet(`v1-campaigns/${campaignId}/sends${qs({
    status: opts.status, engagement: opts.engagement, limit: opts.limit, offset: opts.offset,
  })}`)
  return toPage<CampaignSendRow>(res, 'sends')
}

// Delivery + engagement rollup: unique opens/clicks/unsubscribes and per-link
// click totals for one campaign.
export function getCampaignReport(id: string): Promise<CampaignReport> {
  return apiGet(`v1-campaigns/${id}/report`) as Promise<CampaignReport>
}

// The only path wired to a real inbox before launch. Never touches campaign
// state or the send ledger â€” a test is not a send to a customer.
export function sendCampaignTest(
  id: string,
  to?: string,
): Promise<{ sent: boolean; to: string; note: string; unfilled_markers: string[] }> {
  return apiPost(`v1-campaigns/${id}/test`, to ? { to } : {}) as
    Promise<{ sent: boolean; to: string; note: string; unfilled_markers: string[] }>
}

// Bounded batch. `remaining > 0` means call it again â€” anyone already sent to is
// skipped by the unique send constraint, so repeating is always safe.
export function launchCampaign(id: string): Promise<CampaignLaunchResult> {
  return apiPost(`v1-campaigns/${id}/launch`, {}) as Promise<CampaignLaunchResult>
}

// Scheduling is SENDING, with a delay, and it is gated exactly as launching is:
// the same blockers, and the same typed recipient count in the wizard.
// `scheduled_for` is tenant-local naive (`YYYY-MM-DDTHH:mm`) like every other
// datetime crossing this boundary. campaigns-cron does the sending.
export function scheduleCampaign(
  id: string,
  scheduledFor: string,
): Promise<{ campaign: CampaignRow; recipient_count: number | null; note: string }> {
  return apiPost(`v1-campaigns/${id}/schedule`, { scheduled_for: scheduledFor }) as
    Promise<{ campaign: CampaignRow; recipient_count: number | null; note: string }>
}

// Back to a draft, and the queued time goes with it. Deliberately a plain patch
// rather than its own route: cancelling something that has not happened needs no
// gate, and `draft` is already a legal transition from `scheduled`.
export function unscheduleCampaign(id: string): Promise<{ campaign: CampaignRow }> {
  return updateCampaign(id, { status: 'draft' })
}

// ─── Notifications (v1-alerts) ────────────────────────────────────────────────
//
// The endpoint is v1-alerts, not v1-notifications: that path serves the
// recurring digest SCHEDULES, which are a different feature (configured,
// scheduled, delivered by email/SMS). These are per-event and in-app.
//
// Every route is scoped server-side to the signed-in user, so there is no
// customer_id or user_id parameter to pass — and none to get wrong.

export type NotificationKind =
  | 'booking_created'
  | 'booking_cancelled'
  | 'booking_rescheduled'
  | 'message_received'
  | 'payment_received'

export interface NotificationRecord {
  id: string
  kind: NotificationKind
  title: string
  body: string
  entity_type: 'booking' | 'conversation' | 'payment' | 'customer' | null
  entity_id: string | null
  /** The message inside a conversation that tapping opens. */
  anchor_id?: string | null
  customer_id: string | null
  read_at: string | null
  dismissed_at: string | null
  created_at: string
}

export interface NotificationPage {
  notifications: NotificationRecord[]
  returned: number
  total: number | null
  limit: number
  offset: number
  has_more: boolean
  /** Unread AND undismissed — the badge number. Never the page length. */
  unread: number
}

export function listNotifications(
  opts: { unread?: boolean; includeDismissed?: boolean } & PageOpts = {},
): Promise<NotificationPage> {
  const params = new URLSearchParams()
  if (opts.unread) params.set('unread', 'true')
  if (opts.includeDismissed) params.set('include_dismissed', 'true')
  if (opts.limit !== undefined) params.set('limit', String(opts.limit))
  if (opts.offset !== undefined) params.set('offset', String(opts.offset))
  const qs = params.toString()
  return apiGet(`v1-alerts${qs ? `?${qs}` : ''}`) as Promise<NotificationPage>
}

/** Mark specific notifications read, or every one of mine with `{ all: true }`. */
export function markNotificationsRead(arg: { ids: string[] } | { all: true }): Promise<{ updated: number }> {
  return apiPost('v1-alerts/read', arg) as Promise<{ updated: number }>
}

/** Dismiss — the operator has dealt with it and wants it off the list. */
export function dismissNotifications(arg: { ids: string[] } | { all: true }): Promise<{ updated: number }> {
  return apiPost('v1-alerts/dismiss', arg) as Promise<{ updated: number }>
}

/** `sms` exists only on the booking kinds, and only for operators. */
export type NotificationPreferences = Record<NotificationKind, { in_app: boolean; push: boolean; sms?: boolean }>

/** A team member or staff resource booking texts can go to — never a typed number. */
export interface SmsTargetOption {
  type: 'team_member' | 'staff_resource'
  id: string
  name: string
  /** 'ending 763' — the full number stays on the record. */
  phone_hint: string
}

export interface SmsSettings {
  /** Why no text can go out at all (unpaid, no SMS number), or null. */
  unavailable_reason: string | null
  target: SmsTargetOption | null
  /** The saved choice can no longer receive texts. */
  target_problem: string | null
  options: SmsTargetOption[]
  /** Built by the same code that sends, so the screen cannot misquote it. */
  example: string
  max_chars: number
}

export interface NotificationPreferencesResult {
  preferences: NotificationPreferences
  /** Operators only; absent for a customer and from an older server. */
  sms?: SmsSettings
  /**
   * The VAPID public key, or null when the platform has no keys configured.
   *
   * null is an honest answer, not a failure: it means Web Push is not set up for
   * this deployment, and the settings card must say so rather than offering an
   * Enable button that cannot work. The public half is sent to the push service
   * by every subscribing browser, so it is not a secret.
   */
  push_public_key: string | null
}

export function getNotificationPreferences(): Promise<NotificationPreferencesResult> {
  return apiGet('v1-alerts/preferences') as Promise<NotificationPreferencesResult>
}

/** `smsTarget` is sent only when given: null clears the choice, absent leaves it alone. */
export function updateNotificationPreferences(
  preferences: Partial<NotificationPreferences>,
  smsTarget?: { type: SmsTargetOption['type']; id: string } | null,
): Promise<{ preferences: NotificationPreferences }> {
  const body = smsTarget === undefined ? { preferences } : { preferences, sms_target: smsTarget }
  return apiPatch('v1-alerts/preferences', body) as Promise<{ preferences: NotificationPreferences }>
}

/** Register this browser/device for Web Push. The shape is what PushSubscription.toJSON() emits. */
export function registerPushSubscription(
  sub: { endpoint: string; keys: { p256dh: string; auth: string } },
): Promise<{ subscribed: boolean }> {
  return apiPost('v1-alerts/subscriptions', sub) as Promise<{ subscribed: boolean }>
}

export function removePushSubscription(endpoint: string): Promise<{ unsubscribed: boolean }> {
  // request() carries a body on any verb; apiDelete simply never needed one
  // before. The endpoint goes in the body rather than the query string because
  // it is the subscription's credential — a push endpoint URL is unguessable by
  // design, and query strings reach access logs.
  return request('DELETE', 'v1-alerts/subscriptions', { endpoint }) as Promise<{ unsubscribed: boolean }>
}
